/**
 * Translates V2 settings into scoped CSS. Pure: no DOM, no side effects,
 * deterministic output order. Consumed by the canvas (live <style>) and by
 * the publish path (persisted string) — there is exactly one implementation.
 *
 * Plain JS (not TS): this module is imported both by the Vite build
 * (editor-v2) and by the Vue-2 webpack build — the latter cannot resolve
 * .ts files. Types live in cssEmitter.d.ts (precedent: elementSchemas.js).
 *
 * Selector shape (Fix-Runde 1, task-4 gap; maxWidth moved Fix-Runde 3, I3): a
 * `kind: 'container'` node's real DOM is `section[data-v2-id] > .container >
 * [data-v2-row] > [data-v2-col]` — three selectors are involved, not one:
 *   - `[data-v2-id="ID"]` — the section itself (padding-top/bottom and
 *     `display:none` specifically — see the un-hide note below);
 *   - `[data-v2-id="ID"] > * > [data-v2-row]` — the row two levels below
 *     (everything that establishes/governs the flex context: display other
 *     than 'none', flexDirection, flexWrap, alignItems, justifyContent,
 *     textAlign, gap, AND maxWidth's `max-width`/`margin-inline:auto` pair —
 *     moved here from the section in Fix-Runde 3, Important I3: `.v2-bg`
 *     — the background layer — is a direct CHILD of the SECTION, not of the
 *     row, so a `max-width` on the section clipped the background to the
 *     narrowed content width instead of leaving it full-bleed. Putting the
 *     cap on the row instead constrains only the actual content, exactly
 *     what the retired Bootstrap wrapper this replaces used to do);
 *   - `[data-v2-id="ID"] > * > [data-v2-row] > [data-v2-col="n"]` — one
 *     column (span/order/position).
 * `data-v2-row="true"` is stamped on the row by v2ContainerUtils.ts's
 * applySettings (same step that backfills data-v2-col). The doubled child
 * combinator is load-bearing, not stylistic — it keeps a container's own
 * rules from also matching a NESTED v2-container's row/columns (which,
 * because a column can itself contain another whole v2-container snippet,
 * always sit at least 4 levels further down); see emitNodeCss's own comment
 * for the full reasoning. A `kind: 'element'` node has no row at all — every
 * declaration stays on its own `[data-v2-id]` selector.
 *
 * Layout mode (Fix-Runde 2): the row's default `display` is `grid` (a
 * 12-track `repeat(12, minmax(0, 1fr))` model — see DEFAULT_DECLARATIONS),
 * not `flex` — `grid-column:span N` (column width) only ever means anything
 * in a grid context, and grid is where `span`/`order`/`gap`/`align-items`
 * all behave the way the settings vocabulary describes. Explicit
 * `display:flex` (still a whitelisted, reachable value — existing
 * containers may carry it) is fully supported per TIER: `span` is expressed
 * as `flex:0 0 calc(N / 12 * 100%)` instead of `grid-column:span N` for any
 * tier whose EFFECTIVE (cascade-resolved) display is `flex` — see
 * `effectiveDisplayByTier`/`spanDeclarationsByTier`. Of the properties this
 * module can emit on the row, `flex-wrap` and `flex-direction` are the ones
 * that only ever mean something in a flex context — CSS Grid has no
 * equivalent to either (grid items simply fill the defined template; there
 * is no wrapping concept the way flex has one, and `grid-auto-flow`, not
 * `flex-direction`, is grid's closest analogue and is not part of this
 * settings vocabulary at all). `justify-content`, `align-items`, and `gap`
 * are Box Alignment / Box Sizing properties that work identically in both
 * layout modes and need no special-casing. This module does NOT gate
 * `flex-wrap`/`flex-direction` emission on the tier's effective display
 * being `flex` (unlike `span`, which structurally cannot mean anything
 * under grid) — both are still whitelisted, reachable settings, and the
 * scope of this round was the CONCRETE regression `span` had (grid-column
 * doing nothing under flex); an untouched-except-for-flex-direction
 * container is a real, if narrower, case where emitting a value CSS Grid
 * ignores is dead-but-harmless, same as before this round, not a new
 * problem introduced by it. Flagged for whoever finalises `v2-base.scss`/
 * the settings panel to reconsider (see the task-4 report's Fix-Runde 2
 * section).
 *
 * Fix-Runde 5 ("justify-flex" task) — distribution switches to flex: a
 * further, deliberate exception to "grid is the default". Under THIS
 * module's grid form, `grid-template-columns` is always the fixed 12-track
 * `repeat(12, minmax(0, 1fr))` list (DEFAULT_DECLARATIONS) — `1fr` tracks
 * always consume 100% of the row's width regardless of how many tracks a
 * column's `span` actually occupies, so there is never any free space left
 * for `justify-content` to distribute. Every `justify-content` value but the
 * default `flex-start` is therefore INERT under this grid: two `span:4`
 * columns simply sit flush left in tracks 1-8, leaving tracks 9-12 empty —
 * `space-between` (or any other value) has nothing to push apart. The fix: a
 * tier whose EFFECTIVE (cascade-resolved) `justifyContent` deviates from
 * `flex-start` switches that SAME tier's effective display to `flex` —
 * reusing the flex form this module already fully supports end-to-end (span
 * as `flex-basis`, see `spanDeclarationsByTier`) rather than inventing a
 * second distribution mechanism. `effectiveJustifyContentByTier` is the
 * `justifyContent` analogue of `effectiveDisplayByTier`'s own cascade, and
 * `applyJustifyOverride` is the single place both functions apply this rule
 * (so it cannot drift between the two call sites).
 *
 * Precedence (deliberate, in this exact order):
 *   1. An ACTUALLY hidden tier (`display` cascading to `'none'`, whether set
 *      explicitly at THIS tier or inherited from a lower one) is NEVER
 *      un-hidden or otherwise touched by this override — hiding always wins
 *      over a distribution. A hidden tier's row never gets `display:flex`
 *      pushed onto it (though other row properties, e.g. `justify-content`
 *      itself, may still ride along dead-but-harmless while hidden — same
 *      trait already accepted for flex-wrap/flex-direction above).
 *   2. An explicit `display:'block'` (or any value other than `'grid'`/
 *      `'flex'`/`'none'` this whitelist ever allows) also always wins — the
 *      override below only ever fires FROM `'grid'`.
 *   3. An ALREADY-`'flex'` tier (an explicit choice, made before or
 *      independent of any distribution) stays flex regardless of anything
 *      below — there is no "switch" to gate: it was never grid to begin
 *      with.
 *   4. Otherwise (the tier is currently `'grid'`, explicit or the true
 *      default) — LIMITATION, added in the review round after this task's
 *      first pass: CSS Grid's `grid-template-columns:repeat(12,
 *      minmax(0,1fr))` SUBTRACTS the row's `gap` before dividing the
 *      remaining width across 12 tracks; the flex span form
 *      (`flex:0 0 calc(N / 12 * 100%)`) does NOT — the SAME `gap` is then
 *      added again, between columns, by flex layout itself. For a row whose
 *      columns already sum to the full 12 tracks (the common case — most
 *      rows are built to fill their container) that extra
 *      `gap · (columnCount − 1)` has nowhere to come from: the row
 *      overflows, and because `v2-base.scss` sets `flex-wrap:wrap`
 *      unconditionally, the last column silently wraps onto its own line.
 *      (Review-measured: three `span:4` columns at `gap:'m'`/
 *      `maxWidth:'xxl'` need 1093px, only 1045px are available — the third
 *      column wraps.) The fix is a GATE
 *      (`canAutoSwitchByTier`/`summedSpanByTier`/
 *      `effectiveFlexDirectionByTier`): the automatic switch only fires when
 *      the tier's summed column span is STRICTLY LESS than 12 (columns do
 *      not already fill the row — a full flex line has no free space left
 *      for a distribution to act on anyway, so gating it out costs the
 *      feature nothing) AND the tier's effective `flexDirection` is `'row'`
 *      OR `'row-reverse'` (Fix Round 2 widened this from `'row'` alone —
 *      see the "Fix Round 2" section below for why `row-reverse` is no
 *      longer lumped in with `column`/`column-reverse` here). `column`/
 *      `column-reverse` turn the row into a vertical stack that voids the
 *      span-derived widths and re-points `justify-content` to the vertical
 *      axis; both remain one click away in the panel and do nothing today
 *      (a deliberate, documented decision — see "Fix Round 2" below — not
 *      an oversight), so existing data plausibly already carries one — the
 *      switch must not silently make such a container start behaving
 *      differently the moment ANY distribution is picked. Where the gate
 *      blocks the switch, the distribution stays exactly as inert as it
 *      always was — never a regression, just the pre-existing limitation,
 *      now documented instead of surprising. `flexDirection` (and
 *      `flexWrap`) themselves are NEVER suppressed by this gate — only the
 *      AUTOMATIC `display` switch is gated; an explicit `display:'flex'`
 *      container with `flexDirection:'column'` (or S = 12) keeps rendering
 *      exactly as it does today (case 3 above).
 *
 * Resolution happens PER TIER against the cascaded/effective value, not
 * against whatever (if anything) that tier's OWN `settings.justifyContent`
 * entry carries: `{base:'flex-start', md:'space-between'}` stays grid at
 * base and switches to flex from `md` upward, including every tier ABOVE
 * `md` that carries no explicit `justifyContent` entry of its own at all. A
 * later tier that returns its effective `justifyContent` to `flex-start`
 * switches back to grid (re-emitting `display:grid` — and, per the existing
 * pairing rule, `grid-template-columns` alongside it — rather than silently
 * inheriting `flex` from the tier below); see the "Distribution schaltet auf
 * Flex" test suite in cssEmitter.spec.ts for the exact cases (base-only,
 * md-only with the span form flipping at md, switching back at a higher
 * tier, `display:'none'` plus a distribution, a distribution alongside an
 * explicit `display:'flex'` with no double emission, the S = 12 gate, the
 * `flexDirection !== 'row'` gate, an explicit `display:'block'` winning, and
 * an already-explicit `display:'flex'` at S = 12 staying flex unchanged).
 *
 * `flex-wrap`/`flex-direction` need NO change to their OWN emission for this:
 * both are already emitted unconditionally regardless of a tier's effective
 * display (see the Fix-Runde 2 paragraph above) and simply become
 * meaningful the moment a switched tier is actually `display:flex` —
 * verified, not just assumed, when this round was implemented.
 * `flexDirection` additionally now GATES the automatic switch (case 4
 * above) without its own emission changing at all.
 *
 * Fix Round 2 ("`flex-direction:row-reverse` does nothing" defect) — a
 * SECOND, INDEPENDENT reversal mechanism, preferred over the flex switch
 * above for `row-reverse` specifically: `justifyContent`'s flex switch
 * (Fix-Runde 5) made a REPORTED symptom of the "grid is inert for X" family
 * go away for distributions, but Fix-Runde 5's own review round (I2) then
 * had to GATE that switch away from firing for `row-reverse`/`column`/
 * `column-reverse`, out of caution — with no distribution present at all
 * (the reported case), the switch never fires in the first place, so a bare
 * `flexDirection:'row-reverse'` stayed exactly as inert under grid as
 * before either fix ever existed. Two ways to make it real were evaluated:
 *
 *  (a) Widen the flex-switch trigger itself: make a non-default
 *      `flexDirection` ALSO force the grid→flex switch, mirroring
 *      `justifyContent`. Rejected as the PRIMARY mechanism: it inherits the
 *      exact overflow/wrap hazard `canAutoSwitchByTier`'s span-sum gate
 *      exists for (Fix-Runde 5's own C1) — a switch-driven fix for
 *      `row-reverse` would therefore ALSO have to stay blocked at a summed
 *      span of 12, i.e. the overwhelmingly common "columns fill the row"
 *      shape, which is exactly the shape a user reaching for `row-reverse`
 *      is most likely to have.
 *  (b) CSS Grid can reverse the VISUAL order of auto-placed items natively,
 *      with no mode switch at all: `direction:rtl` on the grid container
 *      flips which physical side grid line 1 sits on (auto-placement still
 *      fills lines 1..N in DOM order, so the whole run mirrors
 *      left↔right), and `direction:ltr` restored on each grid ITEM
 *      (`[data-v2-col]`) undoes the bidi flip for that column's own content
 *      — `direction` is inherited, so nested text/elements/nested
 *      v2-containers inside the column see plain `ltr` again, while any
 *      NESTED row that itself needs reversal simply sets its own explicit
 *      `direction:rtl` on its own selector, which (being explicit, not
 *      inherited) wins over the ancestor's `ltr` the same way any explicit
 *      CSS declaration wins over an inherited one — the mechanism composes
 *      at arbitrary nesting depth with no special-casing needed. Checked
 *      before committing to this (per the task's own instruction to
 *      evaluate, not assume):
 *        - Column PLACEMENT only: `span`'s grid form (`grid-column:span N`)
 *          is untouched — a column still occupies exactly N tracks, only
 *          WHICH physical side of the row those tracks sit on flips. No
 *          interaction with `spanDeclarationsByTier` is needed.
 *        - `justify-content`: the four reachable values other than
 *          `space-between`/`space-around` alias to CSS Box Alignment's
 *          `start`/`end`, which `direction` (an inline-axis property) DOES
 *          relativise — under `direction:rtl`, `flex-start` now packs
 *          columns to the visual RIGHT instead of the left. This is not a
 *          bug to route around: it is the EXACT parity flexbox's own
 *          `flex-direction:row-reverse` already has with `justify-content`
 *          (real flex also re-relativises `flex-start`/`flex-end` when the
 *          direction reverses) — an untouched `justify-content` combined
 *          with `row-reverse` should visually pack to the opposite side by
 *          default, and this mechanism does exactly that with no extra
 *          code.
 *        - Sticky columns: `position:sticky`'s paired `align-self:flex-end`
 *          push (`columnDeclarationsByTier`) is a CROSS-axis (block-axis,
 *          vertical) property for a row-direction grid; `direction` only
 *          ever relativises the INLINE axis, so sticky columns are
 *          unaffected.
 *        - `order`: unaffected — `order` reorders items in DOM-order-space
 *          before placement runs; `direction` then decides which physical
 *          side that (already-reordered) sequence starts from. The two
 *          compose normally, same as in real flexbox.
 *      No dealbreaker found — (b) is used for `row-reverse` INSTEAD of (a):
 *      `gridRowReverseByTier`/`rowDirectionDeclarationsByTier`/
 *      `firstGridRowReverseTier` below. It has NO span-sum/wrap hazard at
 *      all (there is no mode switch, so `canAutoSwitchByTier`'s gate is
 *      simply not consulted for this path) — see the module's own tests for
 *      `row-reverse` at a summed span of 12 working identically to a
 *      summed span well under 12. The flex switch itself is KEPT, narrowed
 *      to its original purpose: distributions (`justifyContent`) still use
 *      it (with `row`/`row-reverse` BOTH now allowed through the gate,
 *      widened in this round — see `canAutoSwitchByTier`'s own comment for
 *      why this is safe now that `row-reverse` has its own correct,
 *      independent handling instead of silently piggy-backing on whatever
 *      the switch happens to do).
 *
 *      `column`/`column-reverse` DECISION (deliberate, not an oversight):
 *      left fully inert, exactly as before this round — no grid-native
 *      trick (grid has no concept of "stack rows top-to-bottom" that
 *      preserves `span`'s WIDTH meaning; a vertical stack would need
 *      `span` to mean a HEIGHT fraction instead, a materially different
 *      feature) and still excluded from the flex-switch gate (unchanged
 *      from Fix-Runde 5's own I2: under `flexDirection:column`, the
 *      span-derived `flex:0 0 calc(N/12*100%)` sets a size on the BLOCK
 *      axis, so spans stop meaning widths the moment the switch would
 *      fire). Implementing `column`/`column-reverse` properly — spans
 *      becoming full-width and the flex-basis mapping moving to the block
 *      axis — is a materially different, larger feature (a second span
 *      vocabulary, not a reversal trick) and out of THIS round's scope; the
 *      panel now says so explicitly instead of leaving the user guessing
 *      (`V2ContainerSettingsControl.vue`'s new `directionHint`).
 */

export const TIERS = ['base', 'sm', 'md', 'lg', 'xl', '2xl'];

export const TIER_MIN_WIDTH = {
  sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536,
};

export const SPACING_STEPS = ['none', 'xs', 's', 'm', 'l', 'xl', '2xl'];

// Whitelists. A value that is not listed here never reaches the stylesheet.
// Exported (Spec B1, task-4): VALUE_DESCRIPTORS references these keys
// directly as `values` — exporting is a visibility change only, the object
// itself and every value gate that reads it are unchanged.
export const ENUM_VALUES = {
  display: ['flex', 'grid', 'block', 'none'],
  // 'row-reverse'/'column-reverse' widened back in (Fix-Runde 2, Important
  // finding I2-neu): both are reachable, real values (the v2 union carried
  // them, the panel used to offer them, and native `flex-direction` accepts
  // them without any extra CSS) — narrowing them out here silently dropped
  // an editor's existing choice on the next normalise/apply cycle.
  flexDirection: ['row', 'column', 'row-reverse', 'column-reverse'],
  flexWrap: ['wrap', 'nowrap'],
  alignItems: ['stretch', 'flex-start', 'center', 'flex-end'],
  justifyContent: ['flex-start', 'center', 'flex-end', 'space-between', 'space-around'],
  position: ['static', 'relative', 'sticky'],
  textAlign: ['left', 'center', 'right'],
  // Task 6 Fix-Runde 1 (review Important I1): column `alignSelf` — see
  // `columnDeclarationsByTier`'s own comment for why this is whitelisted the
  // same way every other enum-typed tiered property is, and why it carries
  // no base-tier default-suppression baseline.
  alignSelf: ['stretch', 'flex-start', 'center', 'flex-end'],
};

const CSS_PROPERTY = {
  display: 'display',
  flexDirection: 'flex-direction',
  flexWrap: 'flex-wrap',
  alignItems: 'align-items',
  justifyContent: 'justify-content',
  position: 'position',
  textAlign: 'text-align',
};

// Container defaults. A value equal to its default emits no declaration at
// the base tier — the default already applies via the cascade (see the
// design spec's "Cascade" section). This map MUST stay in sync with the
// static base stylesheet:
//   files/wps/_modules_dev/templates/global_snippets2/scss/custom/components/required/v2-base.scss
// That file DOES NOT EXIST YET (a later task) — whoever writes it MUST define
// exactly this, unconditionally, no media query, split across THREE selectors
// (Fix-Runde 4, review-fix3.md's corrected prerequisite list, items 1-3;
// supersedes Fix-Runde 3's two-selector version):
//
// 1. Row defaults — unconditional, no media query, byte-identical to this
//    constant:
//   [data-v2-row] {
//     display: grid;
//     grid-template-columns: repeat(12, minmax(0, 1fr));
//     flex-wrap: wrap;
//     align-items: stretch;
//     flex-direction: row;
//     justify-content: flex-start;
//     gap: var(--wm-space-m, 24px);
//   }
//
// 2. Section padding defaults — Fix-Runde 4 (review-fix3.md, Critical 3):
//    scoped to `[data-wps-v2-container][data-v2-id]`, NOT the bare
//    `[data-v2-id]` Fix-Runde 3 originally documented. Columns carry
//    `data-v2-id` too (see wmId.ts/applySettings) — a bare `[data-v2-id]`
//    selector would put this padding on every COLUMN as well, exactly the
//    bug class the `[data-wps-settings]` filter in useV2CssEmission.ts
//    exists to prevent, reintroduced statically. `[data-wps-v2-container]`
//    is the section's own marker (see cssEmitter.js consumers / applySettings
//    call sites) and no column ever carries it:
//   [data-wps-v2-container][data-v2-id] {
//     padding-top: var(--wm-space-m, 24px);
//     padding-bottom: var(--wm-space-m, 24px);
//   }
//
// 3. Column defaults — Fix-Runde 4 (review-fix3.md, Critical 1): required by
//    the base-tier `span` suppression below (SPAN_DEFAULT/spanDeclarationsByTier)
//    now that it is gated on the row's effective form being `grid` — without
//    this rule, a default (untouched) column's `grid-column` is the CSS
//    initial value `auto`, i.e. one 1/12 track (8.33% wide), not full width:
//   [data-v2-col] {
//     grid-column: span 12;   /* = SPAN_DEFAULT */
//     min-width: 0;           /* grid/flex items default to min-width:auto -> overflow */
//   }
//    This rule is INERT for a column whose row is in the (whitelisted, still
//    reachable) explicit `display:flex` form — `grid-column` means nothing
//    there. That is not a gap: spanDeclarationsByTier's base-tier suppression
//    is ALSO gated on the effective form being `grid` (see its own comment
//    below), so a flex-form base tier still emits its own
//    `flex:0 0 calc(12 / 12 * 100%)` declaration explicitly — the two fixes
//    are a pair, neither alone is sufficient.
//
// This constant is only a mirror so the emitter can suppress redundant
// declarations — it does not itself write any CSS anywhere.
//
// Fix-Runde 2 (coordinator-directed): the row's default `display` changed
// from `flex` to `grid` — the 12-track model (`span`, `order`, `gap`, and
// `align-items` all behave the way the settings describe under CSS Grid,
// which `flex` never fully supported for `span`). `grid-template-columns`
// is a NEW, paired entry: it has no settings field of its own (the 12-track
// count is fixed, not user-configurable) — see the `display` walk in
// `nodeDeclarationsByTier` for how it is paired alongside a genuine (i.e.
// non-suppressed) `display:grid` declaration, rather than compared against
// a settings value the way every other entry here is. `flexDirection`'s and
// `justifyContent`'s defaults are also new this round — both previously had
// no default-suppression at all (a known, explicitly-flagged gap from
// Fix-Runde 1: an untouched container emitted `flex-direction:row` and
// `justify-content:flex-start` unconditionally). Both properties remain
// meaningful under grid too (CSS Grid's Box Alignment support covers
// `justify-content`; `flex-direction`'s row-vs-column choice has no grid
// equivalent and is genuinely flex-only — see the file-level "Fix-Runde 2"
// doc comment below for the fuller analysis of which properties still make
// sense to ever emit once grid is the default).
//
// Fix-Runde 3 (Critical 1): `display`'s ACTUAL default only ever engaged once
// `getDefaultSettings()`/`fillDefaults`/the panel's `createDefaultSettings`
// (v2ContainerUtils.ts / V2ContainerSettingsControl.vue) also default to
// `'grid'` — before this round all three defaulted to `'flex'`, so every real
// container carried an explicit, non-default `display:'flex'` and this
// constant's `'grid'` default never actually suppressed anything in
// production. `paddingTop`/`paddingBottom` are new this round (see above).
// Exported (final review, 0351753a3, Important I2) solely so
// v2BaseSpecificity.spec.ts can drive its "every suppressed default reaches a
// browser" guard off this object's own keys instead of a hand-maintained
// list — a key added here without a matching export change would otherwise
// silently escape that test's coverage the same way the old 4-entry table
// did. Exporting a constant is behaviourally inert; no runtime caller reads
// this differently than before.
export const DEFAULT_DECLARATIONS = {
  display: 'grid',
  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: 'var(--wm-space-m, 24px)',
  paddingTop: 'var(--wm-space-m, 24px)',
  paddingBottom: 'var(--wm-space-m, 24px)',
};

// An ELEMENT (`kind: 'element'`, a leaf node with no row/columns of its own)
// is NOT affected by the row-becomes-a-grid change above — nothing about a
// v2-element is a 12-track grid. An element's `display` default is
// completely independent of `DEFAULT_DECLARATIONS.display` above.
//
// Task 5 (v2-css elements): corrected from 'flex' to 'block'. Before this
// task no production element ever reached this branch (see
// useV2CssEmission.ts's own comment on `kind` detection) — nothing had ever
// verified the value against how a v2-element actually renders. A
// `<section>` (the element's own tag, v2ElementUtils.ts's V2_ELEMENT_SELECTOR)
// is display:block by native default; v2-layout.scss's `.v2-element` rule
// never overrides `display` at all. 'flex' would have been WRONG the moment
// this branch was ever exercised for real: a hidden-then-unhidden element
// (hideMobile/hideDesktop migrated to a `display` tier map, see
// elementSchemas.js#normalizeElementSettingsV3) un-hides by emitting this
// value directly onto the element's own `[data-v2-id]` selector (the same
// one carrying `display:none` at the hidden tier — see
// displayDeclarationsByTier's own comment on why elements need no separate
// un-hide push) — switching a single-child wrapper section to `display:flex`
// changes how its child is sized (flex items shrink-to-fit by default,
// unlike a block child which fills 100% width), a real visual regression
// none of the v2e-* Kern-Set rules (v2-layout.scss) were ever built to
// tolerate. 'block' needs no v2-base.scss backing rule at all (unlike
// DEFAULT_DECLARATIONS.display:'grid', which v2-base.scss's `[data-v2-row]`
// rule exists specifically to back) — it is simply the browser's own
// initial value for a `<section>`, so suppressing it at the base tier is
// always correct with zero CSS required.
const ELEMENT_DEFAULT_DISPLAY = 'block';

// Static max-width fallback per named `maxWidth` size — the second var()
// argument, same legacy-fallback pattern as LEGACY_SPACE_PX below (the real
// `--wm-content-*` scale ships with v2-base.scss; until then this is the
// only usable value, or every `max-width` declaration below would be
// invalid-at-computed-value and compute to `none`).
//
// Derivation (Fix-Runde 1, task-4 gap): this replaces the old Bootstrap
// wrapper feature (`applyContentWidth`'s `CONTENT_WIDTH_MAP`, retired in
// Task 4), which put a `col-xl-N` class (out of 12) on a wrapper inside a
// `.container`. That `.container`'s own max-width at the `xl` breakpoint is
// 1140px (files/wps/_modules_dev/templates/global_snippets2/scss/bootstrap/
// _variables.scss's `$container-max-widths`) — `xl` (not the map's unused
// `xxl` entry) is the widest breakpoint this project's grid actually defines,
// so 1140px is the final, settled container width at any viewport. Each
// size's value below is that breakpoint's `col-xl-N` fraction: N/12 * 1140.
// Exported for manifest parity guard (Spec A5.3, wmTokensManifestParity.spec.ts).
export const CONTENT_WIDTH_PX = {
  s: '665px', // col-xl-7  (7/12 * 1140)
  m: '760px', // col-xl-8  (8/12 * 1140)
  l: '855px', // col-xl-9  (9/12 * 1140)
  xl: '950px', // col-xl-10 (10/12 * 1140)
  xxl: '1045px', // col-xl-11 (11/12 * 1140)
};

/**
 * `maxWidth` declarations for the ROW selector (Fix-Runde 3, Important I3 —
 * moved off the section, see the file header comment), or `[]` when the
 * value is 'full' (no cap — the design's own "nothing for full" rule) or
 * unrecognised (whitelist: an unknown string never reaches the stylesheet).
 * Untiered/base-only (Spec C, Task 5 onReject): unset (`undefined`) and the
 * legitimate `'full'` sentinel are NOT rejections and never report; anything
 * else that fails the whitelist below does, via `reportReject`'s own
 * `'base'`-tier convention for this module's two untiered descriptors (see
 * that function's doc comment).
 */
function maxWidthDeclarations(maxWidth) {
  if (typeof maxWidth !== 'string' || maxWidth === 'full') {
    if (maxWidth !== undefined && maxWidth !== 'full') {
      // reportReject is defined further down this file (Spec C, Task 5) —
      // safe to call from here, same forward-reference shape as
      // resolvePlacementCoords below (only ever actually invoked long after
      // module load, once every function in this file exists).
      // eslint-disable-next-line no-use-before-define
      reportReject('container', 'maxWidth', 'maxWidth', 'base', maxWidth);
    }
    return [];
  }
  const px = CONTENT_WIDTH_PX[maxWidth];
  if (!px) {
    // eslint-disable-next-line no-use-before-define
    reportReject('container', 'maxWidth', 'maxWidth', 'base', maxWidth);
    return [];
  }
  return [`max-width:var(--wm-content-${maxWidth}, ${px})`, 'margin-inline:auto'];
}

const RAW_VALUE_RE = /^-?\d+(\.\d+)?(px|rem|em|%|vh|vw)$/;

// Legacy pixel fallback per named spacing step — the second var() argument,
// e.g. `var(--wm-space-m, 24px)`. The `--wm-space-*` custom-property scale
// referenced by the first argument does not exist yet (it ships with
// v2-base.scss, in a later task); without a fallback, every `gap`/`padding`
// declaration this emitter produces is "invalid at computed-value time" in
// today's browser and silently computes to nothing (padding/gap render as
// 0, not as the chosen step) the moment applySettings stops writing the
// equivalent Bootstrap-class bridge (see v2ContainerUtils.ts). The values
// mirror the DOM bridge's own LEGACY_GAP_PX table (Fix-Runde 2, I1-neu) —
// same numbers, same rationale, now the single source since the DOM bridge
// itself is retired for padding/gap once this scale is the only consumer.
// 'xs'/'2xl' have no historical precedent (the old flat `gap` field only
// ever offered 'default'/'0'/'s'/'m'/'l'); picked as plausible, evenly-spaced
// placeholders. REMOVE this fallback (revert to a bare `var(--wm-space-X)`)
// once the real scale ships — at that point an unresolvable custom property
// can no longer happen, and DEFAULT_DECLARATIONS.gap AND .paddingTop/
// .paddingBottom must all be updated in the same commit (see comment there;
// corrected prerequisite list item 11, review-fix3.md).
//
// Exported (Fix-Runde 3, Important I4): usePaddingDragHandlers.ts derives its
// drag-snap pixel table from this SAME map now, instead of measuring the
// theme's Bootstrap `pt-0..pt-5` classes live via getComputedStyle (which, in
// the shipped theme, resolve to 0/6/12/24/36/72px — four of six steps
// disagreed with what this emitter actually renders, 0/4/8/24/40/64px, so the
// container's padding visibly jumped on drag-release). One table now governs
// both what gets rendered and where the drag snaps.
export const LEGACY_SPACE_PX = {
  none: '0', xs: '4px', s: '8px', m: '24px', l: '40px', xl: '64px', '2xl': '96px',
};

/** Resolves a spacing setting to a CSS value, or null when unusable. */
export function resolveSpace(value) {
  if (typeof value === 'string' && SPACING_STEPS.includes(value)) {
    if (value === 'none') return '0';
    return `var(--wm-space-${value}, ${LEGACY_SPACE_PX[value]})`;
  }
  if (value && typeof value === 'object' && typeof value.raw === 'string'
      && RAW_VALUE_RE.test(value.raw)) {
    return value.raw;
  }
  return null;
}

// Column aspect-ratio whitelist (hero spec 2026-08-14). 'auto' is the
// explicit cascade stopper (same role as height's 'auto'). Ratio only
// matters when nothing else sizes the column (stacked/narrow tiers);
// side-by-side the row's stretch height wins by CSS itself — no gating
// needed here.
// Exported (Spec B1, task-4): VALUE_DESCRIPTORS' 'ratio' entry references
// Object.keys(RATIO_VALUES) directly rather than copying the six strings —
// visibility change only, resolveRatio's own logic is untouched.
export const RATIO_VALUES = {
  auto: 'auto', '1:1': '1 / 1', '4:3': '4 / 3', '3:4': '3 / 4', '16:9': '16 / 9', '9:16': '9 / 16',
};
function resolveRatio(value) {
  return (typeof value === 'string' && RATIO_VALUES[value]) || null;
}

/**
 * Row-gap/column-gap pair for a resolved gap value (user report 2026-08-14,
 * "Content Width verhält sich komisch"): the row is ALWAYS a 12-track grid,
 * so 11 inter-track gaps exist even when a single column spans all 12.
 * Tracks are `minmax(0, 1fr)` and floor at 0, but gaps never shrink — the
 * moment 11·gap exceeds the row's (possibly maxWidth-capped) inline size,
 * every spanning column overflows the row to the right by the difference.
 * Measured live: CI spacingCustom 1rem makes --wm-space-m 96px; with
 * maxWidth 's' (700px via CI) the sole span-12 column computed to
 * 11·96 = 1056px inside a 700px row. row-gap keeps the full step (vertical
 * rhythm between wrapped columns is unaffected by the track count);
 * column-gap is capped so the 11 gaps can never exceed the row width minus
 * a 12px track floor. The outer max(0px, …) guards the calc() turning
 * negative in extremely narrow rows — a negative gap is invalid and would
 * kill the whole declaration at computed-value time. '0' stays the plain
 * shorthand (nothing to clamp, and dedupe/default comparisons in
 * walkTierDedup happen on the resolved value BEFORE this expansion).
 * v2-base.scss's static `:where([data-v2-row])` default rule mirrors this
 * pair — keep both in sync. The spelling below (no calc(), tight commas,
 * tight division) is EXACTLY what dart-sass compiles the SCSS twin to —
 * v2BaseSpecificity.spec.ts's byte-parity guard compares the two.
 */
function gapDeclarations(value) {
  if (value === '0') return ['gap:0'];
  return [
    `row-gap:${value}`,
    `column-gap:max(0px,min(${value},(100% - 12px)/11))`,
  ];
}

/** Whitelists a single enum-typed value, or returns null when unusable. */
function sanitizeEnum(key, value) {
  const allowed = ENUM_VALUES[key];
  if (!allowed || !allowed.includes(value)) return null;
  return value;
}

/**
 * Overlap (Spec 2026-07-31): whitelists the `layer` value — 1|2|3, as a
 * number or a numeric string (the panel's `<select>` delivers a string; a
 * settings object built by hand or migrated from JSON may carry either) — or
 * null for anything else (0, 4+, a non-numeric string). `z-index` only ever
 * has visible effect on a positioned element, so `position:relative` is
 * pushed alongside it at the call site (see `layerDeclarationsByTier` below)
 * — a plain `relative` with no offsets moves nothing on its own, so this is
 * harmless on a container that never actually overlaps anything.
 */
function resolveLayer(value) {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n !== 1 && n !== 2 && n !== 3) return null;
  return String(n);
}

/**
 * `layer` (`z-index`) declarations across all tiers, on the SECTION selector.
 *
 * Mid-cascade `'auto'` RESET (Fix-Runde 1, Spec 2026-07-31 — review-reported
 * Critical, same defect class as `heightDeclarationsByTier`'s own): this used
 * to be a plain `walkTierDedup(settings.layer, resolveLayer, 'z-index',
 * undefined, ...)` call. `resolveLayer('auto')` returns `null`, and
 * `walkTierDedup` treats `null` as "invalid, ignore, leave state untouched"
 * — correct for genuine garbage (`0`, `4`, `'vorne'`, still ignored the same
 * way below) but WRONG for the literal string `'auto'`, `layer`'s own named
 * "no override" sentinel (`FIELD_SCHEMA_DEFAULTS.layer`): `toMinWidthTierMap`
 * legitimately emits it mid-cascade whenever an authored map returns to the
 * default after a narrower tier already went active (authored `{sm:2}`
 * converts to the min-width map `{base:2, md:'auto'}` — verified against
 * `toMinWidthTierMap`'s own cascade math, same derivation as the height
 * example in that function's own comment). Ignoring that `'auto'` left
 * `z-index:2;position:relative` in effect from `md` upward forever —
 * `min-width` media queries are cumulative, omitting a counter-declaration
 * can never un-set an earlier one. Not built on `walkTierDedup` for the same
 * reason `heightDeclarationsByTier` isn't: a `null` result needs to
 * distinguish "invalid, ignore" from "valid RESET instruction", which
 * `walkTierDedup`'s single generic `resolved === null` check cannot express.
 *
 * Final-Review Fix (C1, Critical): the reset emits `z-index:auto` ONLY — it
 * must NOT also reset `position` back to `static`, unlike a first-pass
 * assumption might suggest. The section this rule targets ALWAYS already
 * carries `position:relative` via the `.v2-container` class
 * (`v2-container.scss:4-12`, unconditional, not tied to `layer` at all) —
 * that rule is the CONTAINING BLOCK for `.v2-bg` (`position:absolute;
 * inset:0`), any parallax wrapper, and `.v2-overlay-pos`'s own absolutely-
 * positioned children. This emitted section selector's specificity (0,3,1)
 * beats the class's (0,1,0), so an emitted `position:static` at any tier
 * WIDER than the layer entry would win the cascade and strip that containing
 * block from that tier upward — verified: authored `{base:2}` converts
 * (`toMinWidthTierMap`) to the min-width map `{base:2, sm:'auto'}`, so this
 * would have broken every background/overlay from the `sm` breakpoint
 * (640px) up on ANY container that ever set a base-tier layer at all, not
 * just ones that explicitly reset it. `z-index:auto` alone is sufficient to
 * fully undo the active branch's stacking (an element that is ALREADY
 * positioned, which this one always is via the class, gets no NEW stacking
 * context from `z-index:auto` — it only matters together with a non-static
 * position, and removing the z-index removes exactly that) — there is
 * nothing left for `position` to undo, so touching it at all was both
 * unnecessary and actively harmful. No F6/hidden interplay here unlike
 * height: `z-index` has nothing to do with visibility, so this walk (unlike
 * `heightDeclarationsByTier`) needs no `displayByTier` parameter and no
 * hidden-flag tuple at all — a reset at a hidden tier is simply a reset, no
 * special-casing required.
 */
/**
 * Effective FLOATING active-state of `placement` AT a single tier — a
 * lightweight, standalone mobile-first walk over the min-width `placement`
 * tier map, independent of F6/hidden and of `placementWidth`. Used by
 * `layerDeclarationsByTier` (Fix-Runde 2, Final-Review Critical C2) to
 * decide, at each tier IT emits a declaration on, whether pushing
 * `position:relative` there would fight `placementDeclarationsByTier`'s own
 * `position:absolute` — without duplicating that function's full emission
 * logic (dedupe tuple, width coupling, F6 hidden gate — none of which matter
 * for this one yes/no question). Mirrors exactly the `active`/`raw`/`coords`
 * handling `placementDeclarationsByTier` itself uses for the identical
 * carry-forward semantics (a `'flow'` entry deactivates, a valid coordinate
 * pair activates, garbage is ignored and leaves the carried state
 * untouched).
 */
function isFloatingAtTier(placementTierMap, tier) {
  if (!placementTierMap || typeof placementTierMap !== 'object') return false;
  let active = false;
  for (let i = 0; i < TIERS.length; i += 1) {
    const t = TIERS[i];
    if (placementTierMap[t] !== undefined) {
      const raw = placementTierMap[t];
      if (raw === 'flow') {
        active = false;
      } else {
        // resolvePlacementCoords is a plain function DECLARATION further
        // below in this file (hoisted) — safe to call from here, which only
        // ever runs once layerDeclarationsByTier itself is invoked from
        // nodeDeclarationsByTier, i.e. long after module load has finished
        // and every function in this file is defined.
        // eslint-disable-next-line no-use-before-define
        const coords = resolvePlacementCoords(raw);
        if (coords !== null) active = true;
        // garbage: ignored outright, active stays exactly as carried forward.
      }
    }
    if (t === tier) break;
  }
  return active;
}

function layerDeclarationsByTier(settings, pushSection) {
  const tierMap = settings.layer;
  if (!tierMap || typeof tierMap !== 'object') return;
  let effectiveResolved = null;
  let hasEffective = false;
  TIERS.forEach((tier) => {
    if (tierMap[tier] === undefined) return;
    const raw = tierMap[tier];
    const resolved = resolveLayer(raw);
    const isAutoReset = raw === 'auto';
    if (resolved === null && !isAutoReset) {
      // garbage: ignored, state untouched (Spec C, Task 5: reported here,
      // the exact branch that already silently dropped it).
      // eslint-disable-next-line no-use-before-define
      reportReject('container', 'layer', `layer.${tier}`, tier, raw);
      return;
    }

    const newResolved = isAutoReset ? null : resolved;
    const hadActiveBefore = hasEffective && effectiveResolved !== null;
    const isRepeat = hasEffective && newResolved === effectiveResolved;

    effectiveResolved = newResolved;
    hasEffective = true;

    if (isRepeat) return;

    if (newResolved === null) {
      // z-index only (see this function's own comment, Final-Review C1):
      // NEVER push position:static here — the section is always already
      // position:relative via the .v2-container class, the containing block
      // .v2-bg/parallax/.v2-overlay-pos depend on; resetting position would
      // strip that at every tier from here upward.
      if (hadActiveBefore) pushSection(tier, 'z-index:auto');
      return;
    }

    pushSection(tier, `z-index:${newResolved}`);
    // Fix-Runde 2 (Final-Review, Critical C2): if placement's OWN cascade is
    // already floating at THIS tier, position:relative here would win the
    // SAME property in this (or a later) media query over placement's own
    // position:absolute — un-floating the container. This covers BOTH the
    // same-tier case (placementDeclarationsByTier's later call order already
    // wins in-rule, so this is a no-op there) AND the cross-tier case no
    // call-order trick can fix (layer activating/changing at a WIDER tier
    // than placement's own entry). An element that is already positioned via
    // `position:absolute` needs no separate `position:relative` for its
    // z-index to apply — the two are mutually exclusive on one element, and
    // absolute already establishes a stacking context.
    if (!isFloatingAtTier(settings.placement, tier)) {
      pushSection(tier, 'position:relative');
    }
  });
}

// Overlap+height (Spec 2026-07-31): `verticalAlign` -> the row's own
// `justify-content` value once a tier grows a `min-height` (see
// `resolveHeight`/the height walk below) — same three-way mapping
// v2ContainerUtils.ts's now-retired `.v2-full`/`--v2-valign` DOM bridge used
// to hand to the SCSS (`VALIGN` there), reproduced here byte-for-byte so the
// visual result is unchanged by the retirement.
const VALIGN_JUSTIFY = { top: 'flex-start', center: 'center', bottom: 'flex-end' };

/**
 * Resolves a `height` setting to a `min-height` CSS value, or null (`'auto'`,
 * or anything unrecognised — both mean "no override", i.e. natural height,
 * so nothing is emitted). `'full'` is byte-identical to the retired
 * `.v2-full` class's own rule (v2-layout.scss:
 * `calc(100vh - var(--nav-height, 0px))`) — same custom property, same
 * fallback, so a container migrated off the class renders pixel-for-pixel
 * the same as before.
 */
function resolveHeight(value) {
  if (value === 'full') return 'calc(100vh - var(--nav-height, 0px))';
  if (value === '50vh' || value === '75vh') return value;
  if (value && typeof value === 'object' && typeof value.raw === 'string'
      && RAW_VALUE_RE.test(value.raw)) {
    return value.raw;
  }
  return null;
}

/**
 * Whether `tier` is the exact tier `displayDeclarationsByTier` would push its
 * un-hide `display:block` onto — i.e. THIS tier is visible while the tier
 * immediately below it (in cascade order) was `'none'`. `displayByTier` is
 * `effectiveDisplayByTier(settings)`, the SAME cascade `displayDeclarations
 * ByTier` computes internally (its own `hidden` running flag is exactly "was
 * the tier processed immediately before this one `'none'`", reset to false
 * on every non-`'none'` tier and set true only inside the `'none'` branch —
 * structurally identical to comparing `displayByTier[tiers[i-1]]` against
 * `displayByTier[tiers[i]]` here). `base` can never be an un-hide tier (there
 * is no narrower tier for it to un-hide FROM).
 *
 * Needed by `heightDeclarationsByTier`'s auto-reset (Fix-Runde 1, Spec
 * 2026-07-31, review-reported Critical): a reset that happens to land on the
 * SAME tier the display walk already un-hides at would otherwise push a
 * second, redundant `display:block` — harmless in principle (same value
 * twice never conflicts), but easy to avoid outright by simply not pushing
 * it a second time.
 */
function isUnhideTier(displayByTier, tier) {
  const idx = TIERS.indexOf(tier);
  return idx > 0 && displayByTier[TIERS[idx - 1]] === 'none' && displayByTier[tier] !== 'none';
}

/**
 * `height` declarations across all tiers, on the SECTION selector — replaces
 * the retired `.v2-full`/`--v2-valign` DOM bridge (v2ContainerUtils.ts) with
 * `min-height` plus the flex/valign trio (`display:flex;flex-direction:
 * column;justify-content:<valign>`) that class used to provide via SCSS.
 *
 * F6 coordination (binding, see the module's `displayDeclarationsByTier`):
 * at a tier whose EFFECTIVE display is `'none'` this emits NOTHING — a
 * hidden tier stays hidden, min-height/flex would just be dead weight
 * fighting nothing. At a VISIBLE tier the trio is pushed onto `pushSection`
 * (the SAME array `displayDeclarationsByTier` already pushed this tier's
 * un-hide `display:block` onto, if any) — called from `nodeDeclarationsByTier`
 * strictly AFTER the display walk runs, so within one tier's declaration
 * array the trio always lands LATER than any un-hide value already there,
 * and `display:flex` wins by in-rule order (last declaration of a property
 * wins inside one CSS rule) with no specificity trick needed — exactly how
 * the retired class used to win, just expressed as source order instead of
 * a class's higher specificity.
 *
 * Deliberately NOT built on top of `walkTierDedup`: that helper's dedupe
 * compares only the RESOLVED VALUE between two tiers that each carry their
 * own entry, with no notion of a hidden/visible flag — a height value that
 * happens to be IDENTICAL at two authored tiers (`{base:'50vh', md:'50vh'}`)
 * while the section is hidden at the first and visible at the second would
 * be misread as "no change, skip" by `walkTierDedup`'s own tracking, and the
 * un-hide tier would silently lose its min-height/flex-trio (the exact tier
 * that most needs it — it's the one becoming visible). This walk therefore
 * tracks its own effective state as the TUPLE `(resolved value, hidden)`, not
 * the value alone, so a hidden->visible transition with an otherwise-
 * unchanged value is correctly treated as a genuine change, not a repeat.
 *
 * Mid-cascade `'auto'` RESET (Fix-Runde 1, Spec 2026-07-31 — review-reported
 * Critical): `resolveHeight('auto')` returns `null`, and `null` normally
 * means "invalid, ignore, leave the cascade untouched" (garbage input never
 * emits and never updates state, same rule `walkTierDedup` itself documents).
 * That rule is WRONG specifically for the literal string `'auto'`: it is the
 * field's own named "back to natural height" sentinel (`FIELD_SCHEMA_
 * DEFAULTS.height`), not garbage — `toMinWidthTierMap` (authoredTiers.js)
 * legitimately emits it mid-cascade whenever an authored tier map returns to
 * the default AFTER a narrower tier already went active (e.g. authored
 * `{sm:'75vh', lg:'full'}` converts to the min-width map `{base:'75vh',
 * md:'full', xl:'auto'}` — verified against `toMinWidthTierMap`/
 * `legacyEffectiveValue`'s own cascade math). Treating that `'auto'` as
 * "ignore" left `min-height:full` (or whatever was last active) in effect at
 * EVERY tier from `xl` upward forever — `min-width` media queries are
 * cumulative, so omitting a counter-declaration can never un-set an earlier
 * one. `'auto'` therefore gets its own branch: when it interrupts a
 * genuinely ACTIVE (non-null) prior state, it emits a real
 * `min-height:auto;display:block` reset (`display:block` is the SECTION's
 * own native, unstyled default for a `<section>` — the same value
 * `displayDeclarationsByTier` already pushes for its un-hide case, so this
 * reset returns the section to exactly the same baseline that mechanism
 * already established as "back to normal" elsewhere in this file) — but ONLY
 * once, at the transition; a `'auto'` with nothing active before it (a lone
 * `height:{base:'auto'}`, still tested elsewhere in this suite) has nothing
 * to reset and correctly stays silent, matching "auto emits nothing" for
 * that case. `isUnhideTier` avoids a redundant second `display:block` when
 * the reset coincides with the display walk's own un-hide tier.
 */
function heightDeclarationsByTier(settings, displayByTier, pushSection) {
  const tierMap = settings.height;
  if (!tierMap || typeof tierMap !== 'object') return;
  // `verticalAlign` (Spec C, Task 5): untiered, so a rejection reports at
  // the fixed `'base'` tier (see `reportReject`'s own comment) — but ONLY
  // when the field actually carries a genuinely unusable value; `undefined`
  // (the field's own "unset" state, same as every other optional setting)
  // must NOT be reported, it is not a rejection at all, just absence. This
  // report sits in the exact spot the `|| 'center'` fallback already
  // silently absorbed an invalid value — no new branch, `justify`'s own
  // resolved value is unaffected either way.
  const rawVerticalAlign = settings.verticalAlign;
  if (rawVerticalAlign !== undefined && !VALIGN_JUSTIFY[rawVerticalAlign]) {
    // eslint-disable-next-line no-use-before-define
    reportReject('container', 'verticalAlign', 'verticalAlign', 'base', rawVerticalAlign);
  }
  const justify = VALIGN_JUSTIFY[rawVerticalAlign] || 'center';
  // `effectiveResolved === null` doubles as "no active height currently in
  // effect" both before the walk starts and after an 'auto' reset — the
  // walk's own pre-loop state (nothing has ever been active yet) and a
  // post-reset state are the same thing: natural height, no override.
  let effectiveResolved = null;
  let effectiveHidden;
  let hasEffective = false;
  TIERS.forEach((tier) => {
    const hasOwnEntry = tierMap[tier] !== undefined;
    // Fix-Runde 2 (Final-Review, Critical C1): a tier with no OWN height
    // entry used to be skipped outright — silently dropping an ACTIVE height
    // that only ever went hidden (its one entry sits on a tier the F6 guard
    // below suppresses) and never gets a chance to re-emit once display
    // recovers visibility at a WIDER tier that has no height entry of its
    // own. Visiting the exact un-hide tier too (even without an own entry)
    // lets the CARRIED-forward `effectiveResolved` state re-emit there —
    // `isRepeat` already treats a hidden->visible transition as a genuine
    // change (see the tuple comment above), so this alone is sufficient; no
    // other branch below needs to change.
    if (!hasOwnEntry && !isUnhideTier(displayByTier, tier)) return;

    // Only an OWN entry can introduce a NEW resolved value or an 'auto'
    // reset — a tier visited purely for the un-hide re-emit carries the
    // previous tier's `effectiveResolved` forward unchanged.
    let newResolved = effectiveResolved;
    if (hasOwnEntry) {
      const raw = tierMap[tier];
      const resolved = resolveHeight(raw);
      const isAutoReset = raw === 'auto';
      // Garbage (anything invalid that ISN'T the named 'auto' sentinel):
      // ignored outright, exactly as before — never emitted, never updates
      // the effective state, and never counts as a "visit" for the tuple
      // below either (return before touching hidden/isRepeat/hasEffective).
      if (resolved === null && !isAutoReset) {
        // eslint-disable-next-line no-use-before-define
        reportReject('container', 'height', `height.${tier}`, tier, raw); // Spec C, Task 5
        return;
      }
      newResolved = isAutoReset ? null : resolved;
    }

    const hidden = displayByTier[tier] === 'none';
    const hadActiveBefore = hasEffective && effectiveResolved !== null;
    const isRepeat = hasEffective && newResolved === effectiveResolved && hidden === effectiveHidden;

    effectiveResolved = newResolved;
    effectiveHidden = hidden;
    hasEffective = true;

    if (isRepeat || hidden) return;

    if (newResolved === null) {
      if (hadActiveBefore) {
        pushSection(tier, 'min-height:auto');
        if (!isUnhideTier(displayByTier, tier)) pushSection(tier, 'display:block');
      }
      return;
    }

    pushSection(tier, `min-height:${newResolved}`);
    pushSection(tier, `display:flex;flex-direction:column;justify-content:${justify}`);
  });
}

/**
 * Whether ANY of `height`'s own tier entries is exactly `'full'` — needed by
 * v2ContainerUtils.ts's `applySettings` to gate the fullscreen overlay
 * (`--v2-pos-x`/`--v2-pos-y`/`.v2-overlay-pos`), which used to key off the
 * old scalar `settings.height === 'full'` before `height` became a tier map
 * (Task 1, Spec 2026-07-31). Structural, not a CSS resolution — an entry
 * that resolves to nothing for some OTHER reason (an invalid raw value,
 * say) is irrelevant here; this only answers "did the author pick full
 * height anywhere", which is exactly what the overlay's own eligibility
 * question is (the overlay is a full-bleed background-focus tool, meaningful
 * once the section can plausibly be as tall as the viewport at ANY tier).
 */
export function hasFullHeightTier(heightMap) {
  return !!heightMap && typeof heightMap === 'object'
    && Object.values(heightMap).some((v) => v === 'full');
}

// Free positioning ("Frei positionieren", Spec 2026-07-31): floating a
// container out of normal flow onto an explicit x/y focal point, expressed
// as the standard CSS "position an element's CENTRE, not its corner" idiom —
// `left`/`top` place the box's TOP-LEFT corner at (x%, y%) of the offset
// parent, then `transform:translate(-x%, -y%)` re-centres the box's OWN
// (rendered, possibly unknown-at-authoring-time) width/height back over that
// point.
const PLACEMENT_WIDTH_STEPS = [25, 33, 50, 66, 75, 100];
const PERCENT_RAW_RE = /^\d+(\.\d+)?%$/;

/** Clamped integer 0-100 from a candidate coordinate, or null when unusable. */
function clampPlacementCoord(value) {
  const n = typeof value === 'string' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/**
 * Resolves one tier's raw `placement` entry to `{ x, y }` (both clamped
 * integers) or null when the entry is not a usable coordinate pair — a
 * missing/non-numeric x OR y invalidates the WHOLE entry (there is no
 * meaningful "half a coordinate"). Callers treat null the same way every
 * other whitelist rejection in this module is treated: ignored outright,
 * never emitted, never updates the effective cascade state.
 */
function resolvePlacementCoords(value) {
  if (!value || typeof value !== 'object') return null;
  const x = clampPlacementCoord(value.x);
  const y = clampPlacementCoord(value.y);
  if (x === null || y === null) return null;
  return { x, y };
}

/**
 * Resolves a `placementWidth` value to a CSS `width`, or `'auto'` when
 * unusable (the field's own named default, `FIELD_SCHEMA_DEFAULTS.
 * placementWidth`). Whitelist: only the panel's fixed step buttons (a plain
 * number from `PLACEMENT_WIDTH_STEPS`) or an explicit `{ raw }` PERCENTAGE
 * escape hatch — deliberately narrower than every other `{raw}` field in
 * this module (`RAW_VALUE_RE` also allows px/rem/em/vh/vw): a floated box's
 * width is expressed relative to its OFFSET PARENT (see
 * `placementDeclarationsByTier`'s own C1 comment below), so a fixed unit
 * could trivially overflow that parent at a narrower viewport in a way a
 * percentage cannot — out of this task's scope to support, hence rejected
 * outright rather than silently accepted and then visually clipped.
 */
function resolvePlacementWidth(value) {
  if (PLACEMENT_WIDTH_STEPS.includes(value)) return `${value}%`;
  if (value && typeof value === 'object' && typeof value.raw === 'string'
      && PERCENT_RAW_RE.test(value.raw)) {
    return value.raw;
  }
  return 'auto';
}

/**
 * The `placementWidth` value in effect AT `tier`, read from the min-width
 * `placementWidth` map with its OWN carry-forward (the widest defined tier
 * at or below `tier`, mobile-first, same direction `legacyEffectiveValue`
 * (authoredTiers.js) reads a min-width map in — not reproduced via that
 * import to avoid a circular dependency, `authoredTiers.js` already imports
 * `TIERS` from this module), or `'auto'` when nothing is defined at or below
 * `tier`.
 *
 * Deliberately independent of `placementDeclarationsByTier`'s own walk over
 * `settings.placement`'s tier map below: a width can be set at a WIDER tier
 * than where the floating position itself starts. Verified against the real
 * `toMinWidthTierMap` (Task 1) rather than assumed: authored
 * `placementWidth:{'2xl':33}` with no narrower stopper converts to the
 * min-width map `{base:33}` alone, while `placement` itself might only start
 * floating from `md` upward (e.g. authored `{'2xl':{x,y}, sm:'flow'}`
 * converts to `{md:{x,y}}` — no `base` entry at all, since `'flow'` at `sm`
 * already matches the schema default). At `md`, this lookup must still find
 * `33` carried forward from `base` — walking `settings.placement`'s OWN tier
 * map would never visit `base` at all (it has no entry there), so the width
 * cascade must be resolved completely separately from the placement cascade.
 */
function placementWidthAtTier(widthMap, tier) {
  if (!widthMap || typeof widthMap !== 'object') return 'auto';
  const idx = TIERS.indexOf(tier);
  for (let i = idx; i >= 0; i -= 1) {
    if (widthMap[TIERS[i]] !== undefined) {
      const raw = widthMap[TIERS[i]];
      const resolved = resolvePlacementWidth(raw);
      // Spec C, Task 5: `resolvePlacementWidth` itself never returns 'auto'
      // for a genuinely whitelisted value (a step is always '<n>%', a valid
      // {raw} is always its own percent string) — 'auto' is EXCLUSIVELY its
      // own garbage fallback (see that function's own comment). Reporting
      // only when `i === idx` (this tier's OWN entry, not one carried
      // forward from a narrower tier already visited/reported on its own
      // turn) keeps this to exactly one event per genuinely bad entry.
      if (i === idx && resolved === 'auto') {
        // eslint-disable-next-line no-use-before-define
        reportReject('container', 'placementWidth', `placementWidth.${tier}`, tier, raw);
      }
      return resolved;
    }
  }
  return 'auto';
}

/**
 * `placement`/`placementWidth` declarations across all tiers, on the SECTION
 * selector — free positioning (Spec 2026-07-31): an explicit x/y focal point
 * takes a container fully out of normal flow (`position:absolute`), floating
 * it over whatever sits beneath. Structured exactly like
 * `heightDeclarationsByTier` above (same tuple-dedupe, same reset-once-at-
 * the-transition shape, same F6 hidden-tier gate): walks `TIERS` in
 * ascending order over the min-width `placement` map, tracking the
 * effective `(active, x, y, hidden)` tuple so a value that is STRUCTURALLY
 * identical to what the tier below already established is correctly treated
 * as a repeat and dropped — a plain `resolved === effective` reference check
 * would never dedupe two coordinate OBJECTS with equal x/y, and a hidden ->
 * visible transition with an otherwise-unchanged coordinate pair must still
 * be treated as a genuine change (the un-hiding tier is exactly the one that
 * most needs its declaration), same reasoning height's own tuple carries.
 *
 * C1 (Critical, binding — NEVER regress this): the flow-reset is
 * `position:relative`, NEVER `position:static`. `.v2-container`'s own class
 * rule already makes the SECTION `position:relative` unconditionally
 * (v2-container.scss) — that is the CONTAINING BLOCK `.v2-bg` (the
 * background layer, `position:absolute;inset:0`), any parallax wrapper, and
 * THIS SAME floating idiom's own `position:absolute` (the offset parent an
 * `x%`/`y%` coordinate pair is measured against) all depend on. An emitted
 * `position:static` at this selector's specificity would beat that class
 * rule and silently strip the containing block from every tier at or above
 * the reset — the exact defect class `layerDeclarationsByTier`'s own
 * Final-Review C1 fix already had to correct for `z-index`'s own reset; this
 * walk starts from that lesson already applied, not re-discovers it.
 *
 * Layer interplay (binding, pinned by the "kombinierte layer+placement-
 * Regel" test in cssEmitter.spec.ts): `layer`'s own `position:relative` push
 * (`layerDeclarationsByTier`) and this walk's `position:absolute` can both
 * land in the SAME tier's declaration array when a node sets both `layer`
 * and `placement` at that tier. CSS resolves a repeated property by
 * LAST-declaration-wins within one rule — so this function MUST be called
 * strictly AFTER `layerDeclarationsByTier` in `nodeDeclarationsByTier`'s own
 * call order (see the call site there) for `position:absolute` to reliably
 * win over `position:relative` in the combined case; reversing that call
 * order would silently break floating on any container that also sets a
 * `layer`.
 *
 * Inline-preview contract (binding, Task 4 dependency): the drag/nudge UI
 * this walk is a prerequisite for edits `left`/`top` LIVE, in the canvas,
 * before any settings commit — Task 4 relies on this emitter being the ONLY
 * source of `position`/`left`/`top`/`transform` for a floated node once a
 * drag releases (no DOM bridge, no inline style left behind competing with
 * it), same "single source of truth, no competing legacy writer" contract
 * `heightDeclarationsByTier` already established for `min-height` when it
 * replaced the retired `.v2-full` class.
 *
 * F6 (hidden tiers, same as height): at a tier whose EFFECTIVE display is
 * `'none'` this emits NOTHING at all — floating an invisible box is dead
 * weight fighting nothing, and a reset must not "jump the gun" on an
 * as-yet-invisible tier either. `displayByTier` is the SAME
 * `effectiveDisplayByTier(settings)` instance the caller (`emitNodeCss`)
 * already computed, never recomputed here.
 *
 * Width (bound to this SAME walk, never emitted alone — see
 * `placementWidthAtTier`'s own comment for why its cascade is resolved
 * independently): re-resolved and re-pushed every time the FLOATING branch
 * emits, from `placementWidth`'s own min-width cascade at this tier. The
 * flow-reset branch instead carries a fixed `width:auto` — not a lookup,
 * since "auto" is unconditionally correct there regardless of whatever
 * `placementWidth` happens to say once the box has returned to normal flow.
 *
 * Fix-Runde 1 (review-reported Major): the walk drives itself off the UNION
 * of tiers carrying an entry in EITHER `settings.placement` OR
 * `settings.placementWidth`, not `settings.placement`'s tiers alone. A tier
 * that only appears in the WIDTH map — the coordinate itself is unchanged,
 * still carried forward from a narrower tier — used to never be visited at
 * all: `placementWidthAtTier` was only ever CONSULTED from inside a
 * `placement`-tier iteration, so a width-only change at a tier `placement`
 * has no entry of its own at silently never reached the stylesheet. Because
 * `min-width` media queries are cumulative, the STALE width from the tier
 * below then stayed in effect forever — verified against the real pipeline
 * (authored `placement:{'2xl':{x,y}}` + `placementWidth:{'2xl':33, lg:50}`
 * converts, via `toMinWidthTierMap`, to the min-width maps `placement:
 * {base:{x,y}}` / `placementWidth:{base:50, xl:33}` — the intended 33% from
 * `xl` upward never appeared; the container stayed at 50% forever). The
 * union walk fixes this by re-deriving the FULL absolute declaration (carried-
 * forward coordinates + the tier's own resolved width) at every union tier
 * where either the coordinate pair OR the width actually changed — not just
 * where `placement` itself carries an entry.
 *
 * Garbage placement entries at a UNION tier that only exists because of its
 * OWN width entry (`placement`'s value there is either absent or invalid):
 * the carried-forward coordinate/active state is left untouched (same
 * "ignored outright" rule as before) — the tier is still visited for its
 * WIDTH side, and if the resolved width differs from what is already in
 * effect while still floating, the full rule re-emits with the untouched,
 * carried-forward coordinates and the new width.
 *
 * Fix-Runde 1 (review-reported Minor): a lone `{base:'flow'}` (or any
 * `'flow'` entry with nothing ACTIVE before it in the cascade) must emit
 * NOTHING — there is nothing to reset back to flow FROM. Guarded by
 * `hadActiveBefore`, the same pattern `heightDeclarationsByTier`'s own
 * `'auto'`-reset branch already uses for the identical "reset with nothing
 * to reset" case.
 */
function placementDeclarationsByTier(settings, displayByTier, pushSection) {
  const tierMap = settings.placement;
  const widthMap = settings.placementWidth;
  const hasPlacementMap = !!tierMap && typeof tierMap === 'object';
  const hasWidthMap = !!widthMap && typeof widthMap === 'object';
  if (!hasPlacementMap && !hasWidthMap) return;

  // Union (Fix-Runde 1, Major): see this function's own comment above. A
  // tier belongs in the walk if EITHER map carries an explicit entry there.
  //
  // Fix-Runde 2 (Final-Review, Critical C1): ALSO include the exact tier
  // `display` recovers visibility at (`isUnhideTier`), even with no own
  // placement/width entry — otherwise an authored value that only ever
  // lands on a HIDDEN tier (e.g. `placement:{'2xl':{x,y}}` converts, with
  // nothing narrower to stop it, to the min-width map `{base:{x,y}}`) is
  // silently dropped forever: the walk would never visit any WIDER tier to
  // notice display became visible again and re-emit the carried-forward
  // `active`/`x`/`y` state. `hidden`/`isRepeat` below already treat a
  // hidden->visible transition as a genuine change (same tuple reasoning
  // `heightDeclarationsByTier` uses), and `placementWidthAtTier` already
  // does its own independent mobile-first scan regardless of union
  // membership — so widening this filter alone is sufficient; nothing else
  // in the loop body needs to change.
  const unionTiers = TIERS.filter((tier) => (
    (hasPlacementMap && tierMap[tier] !== undefined)
    || (hasWidthMap && widthMap[tier] !== undefined)
    || isUnhideTier(displayByTier, tier)
  ));

  // Carried-forward placement state: persists across every visited tier,
  // updated ONLY when `settings.placement` itself carries a usable ('flow'
  // or a valid coordinate pair) entry at that tier — a width-only tier, or a
  // garbage placement entry, leaves this untouched, exactly the mobile-first
  // carry-forward every other reader of a min-width map in this module
  // already relies on.
  let active = false;
  let x;
  let y;

  // The tuple actually last ESTABLISHED (for dedupe + the Minor fix's
  // `hadActiveBefore` guard) — distinct from `active`/`x`/`y` above so the
  // "did anything actually change at THIS tier" comparison can run before
  // this tier's own contribution overwrites it.
  let lastActive = false;
  let lastX;
  let lastY;
  let lastWidth = 'auto';
  let lastHidden;
  let hasLast = false;

  unionTiers.forEach((tier) => {
    if (hasPlacementMap && tierMap[tier] !== undefined) {
      const raw = tierMap[tier];
      const isFlow = raw === 'flow';
      const coords = isFlow ? null : resolvePlacementCoords(raw);
      if (isFlow) {
        active = false;
      } else if (coords !== null) {
        active = true;
        x = coords.x;
        y = coords.y;
      } else {
        // Garbage (neither 'flow' nor a usable coordinate pair): ignored
        // outright — `active`/`x`/`y` stay exactly as carried forward.
        // Reported here (Spec C, Task 5), the exact branch that already
        // silently dropped it.
        // eslint-disable-next-line no-use-before-define
        reportReject('container', 'placement', `placement.${tier}`, tier, raw);
      }
    }

    const width = placementWidthAtTier(widthMap, tier);
    const hidden = displayByTier[tier] === 'none';
    const hadActiveBefore = hasLast && lastActive;

    const isRepeat = hasLast
      && active === lastActive
      && hidden === lastHidden
      && (!active || (x === lastX && y === lastY && width === lastWidth));

    lastActive = active;
    lastX = active ? x : undefined;
    lastY = active ? y : undefined;
    lastWidth = width;
    lastHidden = hidden;
    hasLast = true;

    // LATENT (Fix-Runde 3, Final-Review — documented, deliberately NOT
    // fixed): a 'flow' reset landing on a HIDDEN tier is correctly silent
    // here (F6), but if the un-hide tier's own carried state is ALSO
    // already flow (no further own entry), `hadActiveBefore` below is false
    // and the reset stays silent there too — so an earlier ACTIVE floating
    // rule from a narrower, pre-hidden-window tier never gets countered
    // anywhere in the stylesheet and keeps cascading forever. Not
    // authorable today via the panel's visibility checkboxes + placement
    // dropdown, so left undocumented-in-code-only rather than fixed.
    if (isRepeat || hidden) return;

    if (!active) {
      // Flow-reset — see this function's own C1 comment: relative, never
      // static. Fix-Runde 1 (Minor): only when something was actually
      // active before — a lone reset with nothing to reset stays silent.
      if (hadActiveBefore) {
        pushSection(tier, 'position:relative;left:auto;top:auto;transform:none;width:auto');
      }
      return;
    }

    pushSection(
      tier,
      `position:absolute;left:${x}%;top:${y}%;transform:translate(-${x}%, -${y}%)`,
    );
    pushSection(tier, `width:${width}`);
  });
}

/**
 * Element-only `width` (Task 5, parity spec 2026-08-15): an integer percentage
 * (1-100 inclusive) or a custom raw value ({ raw: string }) — anything else
 * (a string, a float, 0, >100) is whitelist-rejected, same "unknown value
 * never reaches the stylesheet" rule every other property here follows. Whole
 * numbers only for percentage mode: a percentage doesn't need sub-integer
 * precision, and integer-only keeps the whitelist simple to reason about.
 * Raw values must match RAW_VALUE_RE (px/rem/em/%/vh/vw).
 */
function resolveElementWidth(value) {
  if (value && typeof value === 'object' && typeof value.raw === 'string'
      && RAW_VALUE_RE.test(value.raw)) {
    return value.raw;
  }
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 100) return null;
  return `${value}%`;
}

/**
 * Element-only `align` (Task 5): box alignment via `margin-inline`, for an
 * element whose `width` has been reduced below 100% — mirrors the retired
 * `.v2e-align-center`/`.v2e-align-right` classes (v2-layout.scss) this
 * replaces. 'left' has no declaration of its own (an element with no
 * `margin-inline` override already sits flush left, the CSS initial state)
 * — only 'center' ('auto' both sides) and 'right' ('auto 0': auto on the
 * left pushes the box to the right, 0 on the right) are real, whitelisted
 * values; anything else (including the legacy '' sentinel for "left") is
 * rejected, same as every other enum-shaped value in this module.
 */
function resolveElementAlign(value) {
  if (value === 'center') return 'auto';
  if (value === 'right') return 'auto 0';
  return null;
}

/**
 * Value descriptors (Spec B1, docs/superpowers/specs/
 * 2026-08-17-token-manifest-vocabulary-design.md) — one entry per
 * settings-visible value gate this module enforces, across all three scopes
 * (`container`, `column`, `element`). DESCRIBES the existing gates for later
 * consumers (Task 5's vocabulary export, Task 6's onReject); it does not
 * change how a single gate decides anything — every `values`/`grammar`
 * field below is a reference to the SAME constant/RegExp the gate function
 * itself already reads, never a copy (see each field's own comment where a
 * constant had no existing export to point at).
 *
 * Placement in the file (not immediately after CSS_PROPERTY, module-scope
 * const TDZ): every constant referenced by a `values`/`grammar` field below
 * (CONTENT_WIDTH_PX, RAW_VALUE_RE, RATIO_VALUES, VALIGN_JUSTIFY,
 * PLACEMENT_WIDTH_STEPS, PERCENT_RAW_RE) must already be initialised at this
 * point in the module's top-to-bottom evaluation — placing this block near
 * the top of the file (where CSS_PROPERTY lives) would throw a
 * temporal-dead-zone ReferenceError the moment the module loads. `gate`
 * fields are plain strings (function names for the source-text guard in
 * v2ValueDescriptors.spec.ts), so they carry no such ordering constraint —
 * only the `values`/`grammar` references do.
 *
 * `gate` names the function that actually implements the whitelist check:
 * usually one of this module's dedicated `*DeclarationsByTier` walks, but
 * for properties gated generically through `sanitizeEnum`/`resolveSpace` (no
 * per-property function exists), `gate` points at that shared resolver
 * instead — still a real, existing function name the completeness guard's
 * "dead descriptor" test can verify.
 *
 * Two properties (`display`, `padding`) legitimately get MORE THAN ONE
 * entry, one per scope that actually emits them (see each gate's own
 * comment above for why) — `values`/`kind`/`gate` are identical across a
 * property's own scoped entries, only `scope` differs.
 */
export const VALUE_DESCRIPTORS = [
  // --- container scope: row-level enums (generic sanitizeEnum gate, walked
  // via nodeDeclarationsByTier's CSS_PROPERTY loop) ---------------------
  {
    prop: 'display',
    scope: 'container',
    kind: 'enum',
    values: ENUM_VALUES.display,
    tierable: true,
    rawAllowed: false,
    gate: 'displayDeclarationsByTier',
  },
  {
    prop: 'flexDirection',
    scope: 'container',
    kind: 'enum',
    values: ENUM_VALUES.flexDirection,
    tierable: true,
    rawAllowed: false,
    gate: 'sanitizeEnum',
  },
  {
    prop: 'flexWrap',
    scope: 'container',
    kind: 'enum',
    values: ENUM_VALUES.flexWrap,
    tierable: true,
    rawAllowed: false,
    gate: 'sanitizeEnum',
  },
  {
    prop: 'alignItems',
    scope: 'container',
    kind: 'enum',
    values: ENUM_VALUES.alignItems,
    tierable: true,
    rawAllowed: false,
    gate: 'sanitizeEnum',
  },
  {
    prop: 'justifyContent',
    scope: 'container',
    kind: 'enum',
    values: ENUM_VALUES.justifyContent,
    tierable: true,
    rawAllowed: false,
    gate: 'sanitizeEnum',
  },
  {
    prop: 'textAlign',
    scope: 'container',
    kind: 'enum',
    values: ENUM_VALUES.textAlign,
    tierable: true,
    rawAllowed: false,
    gate: 'sanitizeEnum',
  },
  // `display` also gates a v2-element's own hide/show tier map (Task 5's
  // migrated `hideMobile`/`hideDesktop`) — same whitelist, same gate
  // function, different scope/default (ELEMENT_DEFAULT_DISPLAY).
  {
    prop: 'display',
    scope: 'element',
    kind: 'enum',
    values: ENUM_VALUES.display,
    tierable: true,
    rawAllowed: false,
    gate: 'displayDeclarationsByTier',
  },

  // --- container scope: overlap/height/placement gates (Spec 2026-07-31) -
  {
    // resolveLayer's own literal checks (`n !== 1 && n !== 2 && n !== 3`)
    // for 1|2|3 — no exported array constant exists to reference here.
    // 'auto' is layerDeclarationsByTier's OWN mid-cascade reset sentinel
    // (isAutoReset, Fix-Runde 1, Spec 2026-07-31) — resolveLayer itself does
    // NOT accept it (resolveLayer('auto') returns null); the wrapper
    // special-cases the literal string and emits a real `z-index:auto`,
    // exactly the "named reset value gated by the *DeclarationsByTier
    // wrapper, not the lower-level resolver" shape the `height` descriptor
    // above already documents for its own 'auto'/heightDeclarationsByTier.
    prop: 'layer',
    scope: 'container',
    kind: 'enum',
    values: [1, 2, 3, 'auto'],
    tierable: true,
    rawAllowed: false,
    gate: 'layerDeclarationsByTier',
  },
  {
    // Untiered (VALIGN_JUSTIFY[settings.verticalAlign] is read as a plain
    // scalar, not a tier map) — feeds heightDeclarationsByTier's own
    // flex/valign trio.
    prop: 'verticalAlign',
    scope: 'container',
    kind: 'enum',
    values: Object.keys(VALIGN_JUSTIFY),
    tierable: false,
    rawAllowed: false,
    gate: 'heightDeclarationsByTier',
  },
  {
    // resolveHeight's own literal 'full'/'50vh'/'75vh' checks plus the
    // {raw} escape hatch (RAW_VALUE_RE) — no exported array constant to
    // reference for the three named literals.
    prop: 'height',
    scope: 'container',
    kind: 'raw',
    values: ['auto', 'full', '50vh', '75vh'],
    grammar: RAW_VALUE_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'heightDeclarationsByTier',
  },
  {
    // resolvePlacementCoords clamps x/y independently to 0-100 (Math.min/
    // Math.max in clampPlacementCoord) — no {raw} escape hatch here.
    prop: 'placement',
    scope: 'container',
    kind: 'range',
    range: [0, 100],
    tierable: true,
    rawAllowed: false,
    gate: 'placementDeclarationsByTier',
  },
  {
    prop: 'placementWidth',
    scope: 'container',
    kind: 'percent-step',
    values: PLACEMENT_WIDTH_STEPS,
    grammar: PERCENT_RAW_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'resolvePlacementWidth',
  },
  {
    // maxWidthDeclarations: a single untiered value (always base tier, no
    // V2TierMap) — 'full' is the explicit no-cap sentinel, not a
    // CONTENT_WIDTH_PX key.
    prop: 'maxWidth',
    scope: 'container',
    kind: 'content-width',
    values: [...Object.keys(CONTENT_WIDTH_PX), 'full'],
    tierable: false,
    rawAllowed: false,
    gate: 'maxWidthDeclarations',
  },

  // --- spacing-step gates: resolveSpace (SPACING_STEPS + {raw} escape) --
  {
    prop: 'gap',
    scope: 'container',
    kind: 'spacing-step',
    values: SPACING_STEPS,
    grammar: RAW_VALUE_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'resolveSpace',
  },
  {
    // Section box-model, top/bottom only at container scope (no
    // padding-inline there — see nodeDeclarationsByTier's own comment).
    prop: 'padding',
    scope: 'container',
    kind: 'spacing-step',
    values: SPACING_STEPS,
    grammar: RAW_VALUE_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'resolveSpace',
  },
  {
    // Element scope additionally carries the 'inline' side (Task 5
    // Fix-Runde 1) — same resolver, same values, one more CSS side.
    prop: 'padding',
    scope: 'element',
    kind: 'spacing-step',
    values: SPACING_STEPS,
    grammar: RAW_VALUE_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'resolveSpace',
  },
  {
    // Column padding (Task 6 Fix-Runde 1): top/bottom/inline, same resolver.
    prop: 'padding',
    scope: 'column',
    kind: 'spacing-step',
    values: SPACING_STEPS,
    grammar: RAW_VALUE_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'resolveSpace',
  },
  {
    prop: 'marginTop',
    scope: 'container',
    kind: 'spacing-step',
    values: SPACING_STEPS,
    grammar: RAW_VALUE_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'resolveSpace',
  },

  // --- element scope: width/align (Task 5) -------------------------------
  {
    // resolveElementWidth's own integer 1-100 bound (`value < 1 ||
    // value > 100`), plus the {raw} escape hatch (RAW_VALUE_RE).
    // KNOWN BESTAND-INCONSISTENCY (Spec Nicht-Ziele, not this round's scope):
    // the settings PANEL clamps this same field's minimum to 10, while the
    // emitter's own gate here accepts down to 1 — the two have never been
    // reconciled; this descriptor documents the emitter's actual gate, not
    // the (narrower) panel behaviour.
    prop: 'width',
    scope: 'element',
    kind: 'raw',
    range: [1, 100],
    grammar: RAW_VALUE_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'resolveElementWidth',
  },
  {
    // resolveElementAlign's own literal 'center'/'right' checks — 'left' is
    // the unwritten default (no declaration at all), not a third whitelisted
    // value, so it is deliberately absent from this list.
    prop: 'align',
    scope: 'element',
    kind: 'enum',
    values: ['center', 'right'],
    tierable: true,
    rawAllowed: false,
    gate: 'resolveElementAlign',
  },

  // --- column scope: span/ratio/order/alignSelf/position -----------------
  {
    // spanDeclarationsByTier's own inline bound (`raw >= 1 && raw <= 12`).
    prop: 'span',
    scope: 'column',
    kind: 'span',
    range: [1, 12],
    tierable: true,
    rawAllowed: false,
    gate: 'spanDeclarationsByTier',
  },
  {
    prop: 'ratio',
    scope: 'column',
    kind: 'ratio',
    values: Object.keys(RATIO_VALUES),
    tierable: true,
    rawAllowed: false,
    gate: 'resolveRatio',
  },
  {
    // Column min height (2026-09-22): the section's height values, walked
    // inline in columnDeclarationsByTier (no reset sentinel needed, 'auto'
    // is a real min-height there).
    prop: 'height',
    scope: 'column',
    kind: 'raw',
    values: ['auto', 'full', '50vh', '75vh'],
    grammar: RAW_VALUE_RE.source,
    tierable: true,
    rawAllowed: true,
    gate: 'columnDeclarationsByTier',
  },
  {
    // No dedicated resolver function: the integer 0-99 bound is validated
    // inline inside columnDeclarationsByTier's own walkTierDedup call
    // (`Number.isInteger(value) && value >= 0 && value <= 99`) — `gate`
    // names the enclosing function, the closest real implementer.
    prop: 'order',
    scope: 'column',
    kind: 'range',
    range: [0, 99],
    tierable: true,
    rawAllowed: false,
    gate: 'columnDeclarationsByTier',
  },
  {
    prop: 'alignSelf',
    scope: 'column',
    kind: 'enum',
    values: ENUM_VALUES.alignSelf,
    tierable: true,
    rawAllowed: false,
    gate: 'sanitizeEnum',
  },
  {
    prop: 'position',
    scope: 'column',
    kind: 'enum',
    values: ENUM_VALUES.position,
    tierable: true,
    rawAllowed: false,
    gate: 'sanitizeEnum',
  },
];

/**
 * Non-independent gates (Spec B1; widened Final-Review Minor M1): functions
 * this module's `*DeclarationsByTier`/`resolve*` inventory sweep (see
 * v2ValueDescriptors.spec.ts) would otherwise flag as "a tor with no
 * descriptor" — documented here instead of silently failing the
 * completeness guard. Two distinct reasons, both covered by this one list:
 *  (a) structural — never themselves whitelist a settings VALUE at all
 *      (`nodeDeclarationsByTier`, `rowDirectionDeclarationsByTier`);
 *  (b) subsumed — DOES whitelist/validate a value, but a VALUE_DESCRIPTORS
 *      entry already names the enclosing `*DeclarationsByTier` WRAPPER as
 *      its `gate` (the "closest real implementer" convention that array's
 *      own doc comment establishes for layer/height/placement), so the
 *      lower-level `resolve*` helper itself would otherwise look like a
 *      second, uncovered tor for the same descriptor.
 */
export const DESCRIPTOR_EXCLUDED_GATES = [
  // (a) Orchestrator: dispatches to the per-property walkTierDedup calls
  // above and to the other *DeclarationsByTier gates (layer/height/
  // placement/display) — it composes their output, it does not itself gate
  // a value.
  'nodeDeclarationsByTier',
  // (a) Structural: emits `direction:ltr`/`direction:rtl` from an
  // internally computed boolean (gridRowReverseByTier's own cascade) — no
  // settings value passes through a whitelist here at all (Fix Round 2's
  // grid-native row-reverse trick).
  'rowDirectionDeclarationsByTier',
  // (b) Subsumed by the `layer` descriptor's own gate, `layerDeclarationsByTier`
  // — that wrapper's own literal 1|2|3 checks plus the `'auto'` mid-cascade
  // reset sentinel it special-cases ON TOP of what resolveLayer itself
  // accepts (see the `layer` descriptor's own doc comment in
  // VALUE_DESCRIPTORS for why the wrapper, not this function, is the
  // registered gate).
  'resolveLayer',
  // (b) Subsumed by the `height` descriptor's own gate, `heightDeclarationsByTier`
  // — same wrapper-vs-helper shape as resolveLayer above (the wrapper adds
  // an `'auto'` reset sentinel resolveHeight itself does not accept).
  'resolveHeight',
  // (b) Subsumed by the `placement` descriptor's own gate,
  // `placementDeclarationsByTier` — resolvePlacementCoords is the x/y-pair
  // clamp the wrapper calls per axis, not a standalone whitelist entry
  // point of its own.
  'resolvePlacementCoords',
];

/**
 * onReject (Spec C, Task 5): a per-CALL listener, not module state that
 * outlives one emission — see `emitNodeCss`'s own comment for the full
 * reasoning on why a module-level `let` is nonetheless the correct
 * mechanism here. `null` (the module's resting state, and the only state
 * that ever exists between calls) means "no listener for the emission
 * currently in progress" — every internal gate below checks this before
 * doing any work, so the no-listener path costs exactly one property read
 * and a falsy check, matching the design's "without a callback: today's
 * behaviour, still, zero overhead" requirement.
 */
let activeReject = null;

/**
 * Wraps a caller-supplied `onReject` in try/catch so a throwing listener can
 * NEVER break emission — the callback runs purely for its side effects (a
 * checker collecting events), and this module's own job (producing CSS) must
 * be unaffected by whatever that listener does. Logs at most ONCE per
 * emission (the `warned` flag lives in this closure, recreated fresh for
 * every `emitNodeCss` call — see there), not once per rejection, so a
 * pathological listener that throws on every single reject cannot flood the
 * console.
 */
function wrapOnReject(onReject) {
  if (typeof onReject !== 'function') return null;
  let warned = false;
  return (event) => {
    try {
      onReject(event);
    } catch (err) {
      if (!warned) {
        warned = true;
        console.warn('[cssEmitter] onReject listener threw; emission continues unaffected', err);
      }
    }
  };
}

/**
 * The `allowed` field for a reject event (Spec C): `values` VERBATIM (Final-
 * Review Minor M2 fix — no `.map(String)`: a checker that cross-references
 * `event.allowed` against this same descriptor's `$defs.<key>.enum` in the
 * generated vocabulary (`v2Vocabulary.ts`) must see the identical JS types,
 * not a second, silently-restringified copy of the same facts. `layer`'s
 * `[1, 2, 3, 'auto']` is the one descriptor this actually changes — every
 * other whitelist in VALUE_DESCRIPTORS already holds plain strings) for a
 * whitelist rejection, or the `grammar` regex source for a rejection of the
 * `{ raw }` escape hatch specifically — detected by shape, not by which
 * branch called in (a raw ATTEMPT is any value that looks like
 * `{ raw: '<string>' }`, whether or not the descriptor's own `rawAllowed`
 * is true; an attempted-but-malformed raw value is exactly the case a
 * checker most needs the grammar string for).
 * Falls back to a `'min-max'` range string for the handful of descriptors
 * with neither `values` nor `grammar` (`placement`, `order`, `span`), and
 * to `[]` only if a descriptor is somehow missing altogether (defensive —
 * every reachable gate has a matching entry, enforced by the completeness
 * guard in v2ValueDescriptors.spec.ts, but a reporting helper must never
 * itself throw on an unexpected shape).
 */
function allowedForReject(descriptor, value) {
  if (!descriptor) return [];
  const isRawAttempt = !!value && typeof value === 'object' && typeof value.raw === 'string';
  if (isRawAttempt && descriptor.grammar) return descriptor.grammar;
  // A fresh copy, not the live VALUE_DESCRIPTORS array reference — same
  // values/types, but a listener can't mutate the shared descriptor.
  if (descriptor.values) return [...descriptor.values];
  if (descriptor.range) return `${descriptor.range[0]}-${descriptor.range[1]}`;
  if (descriptor.grammar) return descriptor.grammar;
  return [];
}

/**
 * Reports one rejected value to the active `onReject` listener (a no-op
 * when there is none, i.e. the overwhelmingly common case — see
 * `activeReject`'s own comment). Called from the SAME branch each gate
 * function already used to silently drop an unusable value (`|| null` /
 * an early `return` / an unmatched whitelist lookup) — never a new branch,
 * so a listener can only ever learn about something this module was
 * already discarding, and adding this call never changes what gets emitted.
 * `scope`/`prop` select the one matching `VALUE_DESCRIPTORS` entry (several
 * props, e.g. `padding`, are declared at more than one scope — see that
 * array's own doc comment — so both are required to disambiguate); `path`
 * is the full settings path INCLUDING tier and side where one applies
 * (`padding.md.top`), `tier` is that same tier alone (or the literal
 * `'base'` for the two untiered descriptors, `verticalAlign`/`maxWidth`,
 * which have no per-tier concept to report at all).
 */
function reportReject(scope, prop, path, tier, value) {
  if (!activeReject) return;
  const descriptor = VALUE_DESCRIPTORS.find((d) => d.scope === scope && d.prop === prop);
  activeReject.onReject({
    scope,
    nodeId: activeReject.nodeId,
    prop,
    path,
    tier,
    value,
    allowed: allowedForReject(descriptor, value),
  });
}

const ID_RE = /^wm[a-z0-9]{8}$/;

/**
 * Walks one property's tier map in ascending tier order and reports, via
 * `pushDecl(tier, declaration)`, every tier that must actually emit a
 * declaration once two suppression rules are applied:
 *
 *  - rule (a), tier inheritance: a value equal to the effective value
 *    carried forward from a lower tier is a repeat and is dropped — the
 *    tier below already produces that value via the cascade;
 *  - rule (b), default suppression: at the base tier only, a value equal
 *    to the property's default is dropped (the default already applies
 *    via the static base stylesheet). This does not extend to higher
 *    tiers: if an intermediate tier changed the value away from the
 *    default, a later tier returning to the default value is a genuine
 *    deviation from that intermediate tier and still emits.
 *
 * Invalid values (rejected by `resolve`) are treated as absent — they are
 * never emitted and never update the effective value, matching the
 * existing sanitisation behaviour (unknown values are dropped, not
 * passed through).
 *
 * `resolve(rawValue, tier)` must return the sanitised CSS value as a string,
 * or null when the raw value is unusable. Comparison happens on this
 * resolved string, so plain values ('flex', 6) and object forms
 * ({ raw: '23px' }) compare correctly regardless of object identity. The
 * second argument (Spec C, Task 5's onReject) is passed for free to every
 * caller — most ignore it (existing single-argument resolvers like
 * `resolveSpace`/`resolveElementWidth` never changed), but a call site that
 * wants to REPORT its own rejection (rather than just silently drop it, the
 * behaviour this function itself still provides regardless) needs to know
 * WHICH tier it is looking at to build that event's `path`/`tier` fields —
 * `walkTierDedup`'s own loop is the only place that tier is known.
 */
function walkTierDedup(tierMap, resolve, cssName, defaultValue, pushDecl) {
  if (!tierMap || typeof tierMap !== 'object') return;
  let effective;
  let hasEffective = false;
  TIERS.forEach((tier) => {
    if (tierMap[tier] === undefined) return;
    const resolved = resolve(tierMap[tier], tier);
    if (resolved === null) return;
    const isRepeat = tier === 'base'
      ? resolved === defaultValue
      : hasEffective && resolved === effective;
    effective = resolved;
    hasEffective = true;
    if (!isRepeat) pushDecl(tier, `${cssName}:${resolved}`);
  });
}

/**
 * The effective (cascade-resolved) `justifyContent` value at EVERY tier —
 * never sparse, unlike `settings.justifyContent` itself. Mirrors
 * `effectiveDisplayByTier`'s own cascade below, but for `justifyContent`,
 * defaulting to `DEFAULT_DECLARATIONS.justifyContent` (`'flex-start'`).
 *
 * Needed by the distribution-switches-to-flex override (module header,
 * Fix-Runde 5) so a tier's flex-mode switch can be resolved against the
 * CASCADED value, not just whatever (if anything) that tier's OWN
 * `settings.justifyContent` entry carries: `{base:'flex-start',
 * md:'space-between'}` must switch from `md` onward, INCLUDING `lg`/`xl`/
 * `2xl`, which carry no explicit entry of their own at all.
 */
// A column with no explicit span at all defaults to `{ base: 12 }`
// (v2ContainerUtils.ts#getDefaultColumnSettings) — full width, the same
// meaning a Bootstrap `col-12` used to carry. Fix-Runde 3 (Critical 1): this
// is `span`'s own default value and, like every other property this emitter
// covers, must be suppressed at the base tier — without this, Critical 1's
// guard test (`emitNodeCss(id, getDefaultSettings(), 'container')` must
// equal `''`) could never pass: `getDefaultSettings()` always carries one
// default column, and span previously had NO suppression at all, so an
// untouched container would still emit `grid-column:span 12` unconditionally.
// Moved up (Fix-Runde 5, review round): also needed by
// `effectiveColumnSpanByTier` below, textually before `spanDeclarationsByTier`
// itself now uses it further down this file.
// Exported for the same reason as DEFAULT_DECLARATIONS above (final review,
// 0351753a3, Important I2): the column-span default lives outside
// DEFAULT_DECLARATIONS, so the test guard needs it separately to check
// `grid-column: span 12` the same derived way.
export const SPAN_DEFAULT = 12;

function effectiveJustifyContentByTier(settings) {
  const tierMap = settings.justifyContent;
  const result = {};
  let effective = DEFAULT_DECLARATIONS.justifyContent;
  TIERS.forEach((tier) => {
    if (tierMap && typeof tierMap === 'object' && tierMap[tier] !== undefined) {
      const resolved = sanitizeEnum('justifyContent', tierMap[tier]);
      if (resolved !== null) effective = resolved;
    }
    result[tier] = effective;
  });
  return result;
}

/**
 * The effective (cascade-resolved) `flexDirection` value at EVERY tier —
 * same cascade shape as `effectiveJustifyContentByTier` above, defaulting to
 * `DEFAULT_DECLARATIONS.flexDirection` (`'row'`).
 *
 * Needed by the auto-switch GATE below (Fix-Runde 5, review round): `
 * row-reverse` reverses column order, and `column`/`column-reverse` turn the
 * row into a vertical stack that voids the span-derived widths entirely and
 * re-points `justify-content` to the vertical axis — none of that is what a
 * user who never touched `flexDirection` is expecting when a distribution
 * they picked silently starts rendering the row as flex. All three
 * non-default values are one click away in the panel and do nothing today
 * (span/justify-content only ever mean something under a tier that is
 * ACTUALLY flex or grid), so existing data plausibly already carries one.
 */
function effectiveFlexDirectionByTier(settings) {
  const tierMap = settings.flexDirection;
  const result = {};
  let effective = DEFAULT_DECLARATIONS.flexDirection;
  TIERS.forEach((tier) => {
    if (tierMap && typeof tierMap === 'object' && tierMap[tier] !== undefined) {
      const resolved = sanitizeEnum('flexDirection', tierMap[tier]);
      if (resolved !== null) effective = resolved;
    }
    result[tier] = effective;
  });
  return result;
}

/**
 * The effective (cascade-resolved) `span` value for ONE column at EVERY
 * tier, defaulting to `SPAN_DEFAULT` (12, full width — a column with no
 * explicit span at all, see `SPAN_DEFAULT`'s own comment). Mirrors
 * `spanDeclarationsByTier`'s own cascade (same `raw`/`Number.isInteger`/
 * `1..12` validation), but starts from the TRUE default instead of
 * `undefined` — needed here because the auto-switch gate below needs a
 * REAL number to sum across every column, including ones that never set
 * `span` at all (which still render at their true default width, 12).
 */
function effectiveColumnSpanByTier(col) {
  const result = {};
  let effectiveSpan = SPAN_DEFAULT;
  TIERS.forEach((tier) => {
    const raw = col.span ? col.span[tier] : undefined;
    if (raw !== undefined && Number.isInteger(raw) && raw >= 1 && raw <= 12) {
      effectiveSpan = raw;
    }
    result[tier] = effectiveSpan;
  });
  return result;
}

/**
 * The summed effective column span at EVERY tier — 0 when there are no
 * (valid) columns at all. Needed by the auto-switch gate below: CSS Grid's
 * `grid-template-columns:repeat(12, minmax(0,1fr))` SUBTRACTS the row's
 * `gap` before dividing the remaining width across 12 tracks; the flex span
 * form (`flex:0 0 calc(N / 12 * 100%)`) does NOT — it computes N/12 of the
 * full row width and then the SAME `gap` is added on top, between columns,
 * by the browser's flex layout. For a row whose columns sum to the full 12
 * tracks (S = 12 — the overwhelmingly common case: columns are meant to
 * fill the row), that extra `gap · (columnCount − 1)` has nowhere to come
 * from and the row overflows its container by exactly that amount; because
 * `v2-base.scss` sets `flex-wrap:wrap` unconditionally, the last column
 * wraps onto its own line instead of overflowing invisibly. (Review-
 * measured: three `span:4` columns at `gap:'m'`/`maxWidth:'xxl'` need
 * 1093px but only 1045px are available — the third column wraps.) See
 * `canAutoSwitchByTier`'s own comment for the fix.
 */
function summedSpanByTier(settings) {
  const columns = Array.isArray(settings.columns)
    ? settings.columns.filter((col) => col && typeof col === 'object')
    : [];
  const perColumnByTier = columns.map((col) => effectiveColumnSpanByTier(col));
  const result = {};
  TIERS.forEach((tier) => {
    result[tier] = perColumnByTier.reduce((sum, colByTier) => sum + colByTier[tier], 0);
  });
  return result;
}

// Directions the auto-switch gate lets through (Fix Round 2 widened this
// from `['row']` alone). `row-reverse` no longer needs to be excluded here:
// it now has its OWN correct, independent handling under grid
// (`gridRowReverseByTier`/`rowDirectionDeclarationsByTier` — see the module
// header's "Fix Round 2" section), so there is nothing left for the switch
// to silently activate that isn't already handled correctly either way (if
// the switch fires, real flexbox's native `row-reverse` support takes over;
// if it doesn't, the grid-native `direction:rtl` trick already does the
// job). `column`/`column-reverse` stay excluded — DELIBERATELY, see the same
// module-header section: a switch would set the span-derived `flex-basis`
// on the wrong (block) axis, and no grid-native trick exists for a vertical
// stack either.
const AUTO_SWITCH_DIRECTIONS = ['row', 'row-reverse'];

/**
 * Whether the distribution-switches-to-flex override is even ELIGIBLE to
 * fire at a given tier — the auto-switch GATE (Fix-Runde 5, review round;
 * widened Fix Round 2, see `AUTO_SWITCH_DIRECTIONS`). Both conditions must
 * hold:
 *
 *  - the tier's summed column span must be STRICTLY LESS than 12 (i.e. the
 *    columns do NOT already fill the row) — see `summedSpanByTier`'s own
 *    comment for why a full-width row (S = 12) overflows and wraps under
 *    the flex form. This is also not a real LOSS at S = 12: a full flex
 *    line has no free space left over for `justify-content` to distribute
 *    in the first place, so gating it out costs the feature nothing.
 *  - the tier's effective `flexDirection` must be one of
 *    `AUTO_SWITCH_DIRECTIONS` (`'row'` or `'row-reverse'`) — see
 *    `effectiveFlexDirectionByTier`'s own comment and the module header's
 *    "Fix Round 2" section for why `column`/`column-reverse` must not
 *    silently gain new, surprising behaviour the moment a distribution is
 *    picked, while `row-reverse` safely can now that it has its own correct
 *    handling either way the switch goes.
 *
 * Where the gate blocks the switch, the distribution stays exactly as inert
 * as it was before this whole feature existed — never a regression, just
 * the pre-existing (reported, but out of THIS fix's scope) limitation for
 * full-width/column-direction rows. This gate only ever governs the
 * AUTOMATIC upgrade from an (explicit-or-default) `'grid'` tier — see
 * `applyJustifyOverride`'s own comment for why an ALREADY-`'flex'` tier
 * (explicitly set that way) is untouched by it.
 */
function canAutoSwitchByTier(settings) {
  const spanSumByTier = summedSpanByTier(settings);
  const flexDirectionByTier = effectiveFlexDirectionByTier(settings);
  const result = {};
  TIERS.forEach((tier) => {
    result[tier] = spanSumByTier[tier] < 12 && AUTO_SWITCH_DIRECTIONS.includes(flexDirectionByTier[tier]);
  });
  return result;
}

/**
 * Applies the distribution-switches-to-flex override (module header,
 * Fix-Runde 5) to one tier's RAW display value. `rawDisplay` is the cascade
 * of `settings.display` alone (or the row/section's own true default, absent
 * any explicit entry) — the value display would have BEFORE this override
 * is considered at all. Shared by `displayDeclarationsByTier` and
 * `effectiveDisplayByTier` so the same rule can't drift between the two call
 * sites.
 *
 * The override only ever fires for `rawDisplay === 'grid'` (the AUTOMATIC
 * switch this whole feature is about) AND `canAutoSwitch` (the gate above)
 * AND a non-default `justifyValue`. This is deliberately narrower than "any
 * display but 'none'":
 *
 *  - `rawDisplay === 'flex'` (an EXPLICIT, pre-existing flex tier) is
 *    already flex — there is no "switch" to gate at all, so it is returned
 *    unchanged regardless of `canAutoSwitch`. A full-width (S = 12) row a
 *    user had ALREADY set to `display:'flex'` themselves keeps behaving
 *    exactly as it does today; the gate is about preventing a NEW,
 *    surprising wrap, not about re-litigating an existing explicit choice.
 *  - `rawDisplay === 'block'` (Review I3): `block` must WIN over the
 *    distribution, same as the module header's own precedence list always
 *    said ("wins over an explicit display value") — before this fix the
 *    guard was merely `rawDisplay !== 'none'`, which incorrectly turned an
 *    explicit `'block'` into `'flex'` too.
 *  - `rawDisplay === 'none'` (hiding) still always wins, unchanged.
 */
function applyJustifyOverride(rawDisplay, justifyValue, canAutoSwitch) {
  if (rawDisplay === 'grid' && canAutoSwitch && justifyValue !== 'flex-start') return 'flex';
  return rawDisplay;
}

/**
 * Whether the grid-native reversal trick (Fix Round 2 — see the module
 * header's "Fix Round 2" section) is active at a given tier: the tier's
 * effective `flexDirection` is `'row-reverse'` AND the tier's effective
 * `display` is (still) `'grid'`. Takes the two per-tier maps rather than
 * `settings` directly so the SAME pair —
 * `effectiveDisplayByTier(settings)`/`effectiveFlexDirectionByTier(settings)`
 * — can be computed ONCE per node and shared by both the row's own
 * declarations (`rowDirectionDeclarationsByTier`) and the columns' `ltr`
 * reset (`firstGridRowReverseTier`), rather than each call site re-deriving
 * the cascade independently and risking drift.
 *
 * Deliberately does NOT fire for `'flex'`: an already-flex tier (explicit,
 * or switched there by a distribution — Fix-Runde 5) already gets a
 * correct, NATIVE `row-reverse` from `flex-direction:row-reverse` itself
 * (unconditionally emitted regardless of form, see the "Fix-Runde 2"
 * section near the top of this file) — layering the grid-only `direction`
 * trick on top would be redundant at best. Does NOT fire for `'block'`/
 * `'none'` either, for the same reason `spanDeclarationsByTier`'s own
 * `SPAN_FORM_BY_DISPLAY` lookup skips them: there is no grid to reverse.
 */
function gridRowReverseByTier(displayByTier, flexDirectionByTier) {
  const result = {};
  TIERS.forEach((tier) => {
    result[tier] = displayByTier[tier] === 'grid' && flexDirectionByTier[tier] === 'row-reverse';
  });
  return result;
}

/**
 * The ROW's own `direction` declarations for the grid-native reversal trick
 * (Fix Round 2). Unlike the column-side reset (`firstGridRowReverseTier`
 * below), the ROW needs the FULL walk-with-resets treatment: `direction` is
 * a real, inherited CSS property applied to the SAME row element across
 * every tier's media query — if the reversal turns off again at a higher
 * tier (e.g. `flexDirection:{base:'row-reverse', lg:'row'}`), the row must
 * explicitly re-emit `direction:ltr` there, or it would stay `rtl` forever
 * once set. Same reasoning `displayDeclarationsByTier` already applies to
 * re-emitting `display:grid` when a switched tier returns to grid.
 */
function rowDirectionDeclarationsByTier(reversedByTier, pushRow) {
  let effective = false;
  let hasEffective = false;
  TIERS.forEach((tier) => {
    const reversed = reversedByTier[tier];
    const isRepeat = tier === 'base'
      ? reversed === false
      : hasEffective && reversed === effective;
    effective = reversed;
    hasEffective = true;
    if (!isRepeat) pushRow(tier, `direction:${reversed ? 'rtl' : 'ltr'}`);
  });
}

/**
 * The first tier (cascade order) at which the grid-native reversal is
 * active, or `null` if never. Every column needs an explicit `direction:ltr`
 * reset from exactly this tier onward (see the module header's "Fix Round
 * 2" section on why nested content stays correctly `ltr`) — but, unlike the
 * row's own declarations above, needs NO "turn it back off" transition:
 * `direction:ltr` is the universal initial value anyway, so a column that
 * keeps its explicit `ltr` even after the row itself reverts to `row` (or
 * anything non-reversed) at some still-higher tier is not a bug, just a
 * harmless no-op from that point on. One shared tier for every column of
 * the row — the reversal state is a ROW-level property, not per-column, so
 * this is computed ONCE per node (`columnEntriesByTier`) rather than
 * re-derived per column.
 */
function firstGridRowReverseTier(reversedByTier) {
  return TIERS.find((tier) => reversedByTier[tier] === true) ?? null;
}

/**
 * `display` declarations across all tiers, split into the row's own layout
 * value (or the section's, for `kind:'element'`) and the SECTION's
 * visibility. Unlike every other property, `display` needs bespoke (not
 * `walkTierDedup`-generic) handling for two related reasons:
 *
 *  - Fix-Runde 1: `'none'` always targets the SECTION — hiding only the row
 *    would leave the section's own padded/backgrounded box visibly empty
 *    instead of truly hidden — while every other resolved value is a layout
 *    value that belongs wherever layout properties go for this `kind` (the
 *    ROW for a container, the section itself for an element, which has no
 *    row at all).
 *  - Fix-Runde 3 (Review Critical 2): a tier whose EFFECTIVE display flips
 *    away from `'none'` (the section was hidden going in) must ALSO push an
 *    explicit `display:block` onto the SECTION at that tier. For a
 *    CONTAINER, the layout declaration itself ('display:grid'/'flex'/etc.)
 *    only ever lands on the ROW — the section has nothing else to un-hide
 *    it, so without this a container hidden at any tier (`{base:'none',
 *    md:'flex'}`, e.g. from legacy `hideMobile` normalisation) stayed
 *    hidden at EVERY tier forever, turning "hide on mobile" into "hidden
 *    always". For an ELEMENT, the layout declaration already lands on the
 *    SAME `[data-v2-id]` selector the visibility lives on, so the plain
 *    `display:<value>` decl already un-hides it on its own — pushing a
 *    second, redundant `display:block` there would fight with (and, being
 *    pushed after, silently overwrite) that real value. The extra push is
 *    therefore only ever done when `kind === 'container'` (i.e. `pushLayout`
 *    is NOT the same function as `pushSection`).
 *
 * `hidden` tracks whether the tier immediately below (or the row's/element's
 * true default, for a tier with no explicit value at all) resolves to
 * `'none'` — independent of whether the LAYOUT declaration itself happened
 * to be suppressed as a repeat/default at this tier (a tier can return to a
 * layout value equal to the (suppressed) default while still needing to
 * un-hide the section, e.g. `{base:'none', md:'grid'}` with `'grid'` the row's
 * own true default — no row declaration is needed at md, but the section
 * still needs its `display:block`).
 */
function displayDeclarationsByTier(settings, kind, pushSection, pushLayout) {
  const tierMap = settings.display;
  const hasTierMap = tierMap && typeof tierMap === 'object';
  const displayDefault = kind === 'container' ? DEFAULT_DECLARATIONS.display : ELEMENT_DEFAULT_DISPLAY;
  // Distribution-switches-to-flex (module header, Fix-Runde 5): container-
  // only — an element's settings carry no `justifyContent` field at all
  // (elementSchemas.js has none), so `justifyByTier` would stay all-
  // `'flex-start'` anyway; gating on `kind` here just skips the (otherwise
  // harmless) extra cascade walk for the case that can never fire.
  const justifyByTier = kind === 'container' ? effectiveJustifyContentByTier(settings) : null;
  // Auto-switch gate (Fix-Runde 5, review round) — see `canAutoSwitchByTier`'s
  // own comment: summed column span < 12 AND effective flexDirection 'row'.
  // Same container-only gate as `justifyByTier` above.
  const autoSwitchByTier = kind === 'container' ? canAutoSwitchByTier(settings) : null;

  // Fix-Runde 5: the walk below visits EVERY tier unconditionally, not just
  // tiers with their own explicit `settings.display` entry — a distribution
  // can switch a tier's effective display even when that tier carries no
  // `display` entry of its own at all (e.g. `justifyContent:
  // {base:'flex-start', md:'space-between'}` with no `display` field
  // anywhere), so the walk can no longer skip such tiers the way the plain
  // `tierMap[tier] === undefined` guard used to (pre-Fix-Runde-5, a tier with
  // no explicit `display` entry was never a candidate for emitting anything
  // display-related at all — that stopped being true the moment `display`
  // could also change because of a DIFFERENT property's value).
  let rawDisplay = displayDefault;
  let effective;
  let hasEffective = false;
  let hidden = false;
  TIERS.forEach((tier) => {
    if (hasTierMap && tierMap[tier] !== undefined) {
      const resolvedExplicit = sanitizeEnum('display', tierMap[tier]);
      if (resolvedExplicit !== null) {
        rawDisplay = resolvedExplicit;
      } else {
        // Spec C, Task 5: reported here, the exact branch that already
        // silently dropped an unusable `display` value. `kind` doubles as
        // the descriptor's own `scope` — `display` is declared at both
        // 'container' and 'element' scope, same values, see VALUE_DESCRIPTORS.
        reportReject(kind, 'display', `display.${tier}`, tier, tierMap[tier]);
      }
    }
    const resolved = kind === 'container'
      ? applyJustifyOverride(rawDisplay, justifyByTier[tier], autoSwitchByTier[tier])
      : rawDisplay;

    const isRepeat = tier === 'base'
      ? resolved === displayDefault
      : hasEffective && resolved === effective;
    effective = resolved;
    hasEffective = true;

    if (resolved === 'none') {
      if (!isRepeat) pushSection(tier, 'display:none');
      hidden = true;
      return;
    }

    if (!isRepeat) {
      pushLayout(tier, `display:${resolved}`);
      if (kind === 'container' && resolved === 'grid') {
        pushLayout(tier, `grid-template-columns:${DEFAULT_DECLARATIONS.gridTemplateColumns}`);
      }
    }
    if (kind === 'container' && hidden) {
      pushSection(tier, 'display:block');
    }
    hidden = false;
  });
}

/**
 * Builds the per-tier map for one padding side out of `settings.padding`.
 * `side` is `'top'`/`'bottom'` (both kinds) or, Task 5 Fix-Runde 1, `'inline'`
 * (element-only, see the `padding-inline` walk in `nodeDeclarationsByTier`) —
 * a plain string key inside the same per-tier object, not a fourth CSS
 * property with its own top-level settings field.
 */
function paddingSideMap(settings, side) {
  if (!settings.padding || typeof settings.padding !== 'object') return null;
  const map = {};
  TIERS.forEach((tier) => {
    const tierPadding = settings.padding[tier];
    if (tierPadding && typeof tierPadding === 'object' && tierPadding[side] !== undefined) {
      map[tier] = tierPadding[side];
    }
  });
  return map;
}

/**
 * Declarations for one node, split by which DOM element they actually style,
 * across all tiers, after dedupe. Returns `{ sectionByTier, rowByTier }` —
 * each a plain object: tier -> array of declaration strings (only tiers with
 * at least one surviving declaration are present). Order within a tier's
 * array is fixed (display/display:block-unhide, then the remaining
 * CSS_PROPERTY keys in object order, then, Fix Round 2, CONTAINER-ONLY the
 * grid-native reversal's `direction` — see `rowDirectionDeclarationsByTier`
 * — then gap, then padding-top/padding-bottom, then, Task 5 Fix-Runde 1,
 * ELEMENT-ONLY padding-inline, then maxWidth — Fix-Runde 3, Important I3:
 * now on the ROW, see below — then, Task 5, ELEMENT-ONLY width/margin-inline)
 * regardless of the settings object's own key insertion order. `displayByTier`
 * (Fix Round 2) is the pre-computed `effectiveDisplayByTier(settings)` result
 * for `kind:'container'`, or `null` for `kind:'element'` (an element has no
 * row/columns to reverse) — passed in by the caller (`emitNodeCss`) so it is
 * computed exactly ONCE per node and shared with the column-side reset
 * instead of being re-derived here independently.
 *
 * The split (Fix-Runde 1, task-4 gap): the real DOM is
 * `section[data-v2-id] > .container > [data-v2-row]` for a container — the
 * id-bearing section is NOT the flex row. Properties that establish/govern
 * the flex context (display other than 'none', flexDirection, flexWrap,
 * alignItems, justifyContent, textAlign, gap, maxWidth) belong on the row;
 * the section's own box-model properties (padding-top/bottom, and
 * `display:none`/its un-hiding `display:block` counterpart — hiding only the
 * row would leave the section's own padded/backgrounded box visibly empty
 * instead of truly hidden, see `displayDeclarationsByTier`) stay on the
 * section. A `kind: 'element'` node has no row at all (v2-elements are leaf
 * nodes, not `.container > row > col` structures), so every declaration
 * stays on its own `[data-v2-id]` selector regardless of which bucket it
 * would occupy for a container (maxWidth itself is container-only and never
 * reaches an element at all — see below).
 */
function nodeDeclarationsByTier(settings, kind, displayByTier) {
  const sectionByTier = {};
  const rowByTier = {};
  const pushSection = (tier, decl) => {
    if (!sectionByTier[tier]) sectionByTier[tier] = [];
    sectionByTier[tier].push(decl);
  };
  const pushRow = (tier, decl) => {
    if (!rowByTier[tier]) rowByTier[tier] = [];
    rowByTier[tier].push(decl);
  };
  const pushLayout = kind === 'container' ? pushRow : pushSection;

  // `display` (incl. the section's un-hide `display:block` counterpart, Fix-
  // Runde 3, Critical 2) needs bespoke handling — see displayDeclarationsByTier.
  displayDeclarationsByTier(settings, kind, pushSection, pushLayout);

  ['flexDirection', 'flexWrap', 'alignItems', 'justifyContent', 'textAlign'].forEach((key) => {
    walkTierDedup(
      settings[key],
      (value, tier) => {
        const resolved = sanitizeEnum(key, value);
        // Spec C, Task 5: all five of these are 'container'-scope-only
        // VALUE_DESCRIPTORS entries (an element's settings never carry any
        // of these fields at all, so this branch is dead for kind:'element'
        // in practice — see `elementSchemas.js` — but the descriptor's own
        // scope, not the node's `kind`, is what `scope` must name here).
        if (resolved === null) reportReject('container', key, `${key}.${tier}`, tier, value);
        return resolved;
      },
      CSS_PROPERTY[key],
      DEFAULT_DECLARATIONS[key],
      pushLayout,
    );
  });

  // Fix Round 2 ("row-reverse" defect) — grid-native reversal: see the
  // module header's "Fix Round 2" section. Container-only (`displayByTier`
  // is `null` for `kind:'element'`, see `emitNodeCss` — an element has no
  // row/columns at all, so there is nothing here to reverse).
  if (kind === 'container') {
    const reversedByTier = gridRowReverseByTier(displayByTier, effectiveFlexDirectionByTier(settings));
    rowDirectionDeclarationsByTier(reversedByTier, pushRow);
  }

  walkTierDedup(settings.gap, (value, tier) => {
    const resolved = resolveSpace(value);
    if (resolved === null) reportReject('container', 'gap', `gap.${tier}`, tier, value); // Spec C, Task 5
    return resolved;
  }, 'gap', DEFAULT_DECLARATIONS.gap, (tier, decl) => {
    gapDeclarations(decl.slice('gap:'.length)).forEach((d) => pushLayout(tier, d));
  });

  // Padding is always the section's own box-model, regardless of `kind` — a
  // container's section is visually equivalent to its sole content child for
  // top/bottom padding (see v2ContainerUtils.ts's applySettings doc comment).
  // Fix-Runde 3 (Critical 1): the default (base-tier suppression) value is
  // now DEFAULT_DECLARATIONS.paddingTop/paddingBottom, not `undefined` —
  // before this fix padding-top/bottom rendered UNCONDITIONALLY regardless of
  // whether the value equalled its own default ('m'), which alone defeated
  // Critical 1's guard test (an untouched, all-default container must emit
  // nothing at all).
  //
  // Task 5 (v2-css elements): the suppression baseline is CONTAINER-ONLY.
  // v2-base.scss backs `DEFAULT_DECLARATIONS.paddingTop/paddingBottom` with a
  // real rule scoped to `[data-wps-v2-container][data-v2-id]` — a plain
  // v2-element selector is NOT covered by that rule (correctly: an element's
  // own untouched default is zero padding, not 'm'). Comparing an ELEMENT's
  // padding against the container's 'm' baseline would silently suppress a
  // genuinely-set 'm' value (elementSchemas.js's padding step '3') the
  // instant a user picked exactly that step — the exact "suppression with no
  // backing rule" trap this project has hit before: the declaration simply
  // never reaches the page, and no jsdom test catches it (jsdom doesn't
  // evaluate `var()`, so only a literal-string assertion would). `undefined`
  // as the default for `kind === 'element'` disables suppression entirely
  // (same "no notion of a default to compare against" as `order`'s own walk
  // above), matching the fact that no CSS rule anywhere claims a default
  // element padding at all.
  // No nested ternaries: the Vue-2 host lints this file (no-nested-ternary)
  // and fails its webpack build on them.
  const CONTAINER_PADDING_DEFAULT = {
    top: DEFAULT_DECLARATIONS.paddingTop,
    bottom: DEFAULT_DECLARATIONS.paddingBottom,
  };
  ['top', 'bottom'].forEach((side) => {
    const cssDefault = kind === 'container' ? CONTAINER_PADDING_DEFAULT[side] : undefined;
    walkTierDedup(
      paddingSideMap(settings, side),
      (value, tier) => {
        const resolved = resolveSpace(value);
        // Spec C, Task 5: `kind` doubles as the descriptor's own scope here
        // — `padding` is declared at 'container' and 'element' scope (this
        // walk runs for both), `path` carries the side the same way the
        // event example in the design spec does (`padding.md.top`).
        if (resolved === null) reportReject(kind, 'padding', `padding.${tier}.${side}`, tier, value);
        return resolved;
      },
      `padding-${side}`,
      cssDefault,
      pushSection,
    );
  });

  // Task 5 Fix-Runde 1 (review Important I1): `padding-inline`, ELEMENT-ONLY.
  // The retired `.v2-element` legacy rule this migration replaces
  // (v2-layout.scss, `padding: calc(var(--wm-space, 0.25rem) *
  // var(--v2e-pad, 0))`) was the `padding` SHORTHAND — all four sides, not
  // just top/bottom. Checked before deciding (per the review's instruction):
  // that legacy rule sits on the same `section.section.v2-element` box that
  // also carries `width:100%`/`width:var(--v2e-w, 100%)`; nothing about that
  // box removes it from normal flow or otherwise makes horizontal padding a
  // no-op, so the shorthand genuinely inset the content on the left/right too
  // — this was real, visible padding, not dead weight. Emitting only
  // padding-top/padding-bottom (as this module did before this fix round)
  // silently dropped it for any migrated element with a non-'none' padding
  // step. `padding-inline` (not left/right longhands) mirrors the emitter's
  // existing logical-property convention (`margin-inline` below, for
  // `align`) and needs only ONE declaration instead of two. Sourced from
  // `settings.padding[tier].inline` — a third per-tier key alongside
  // top/bottom (see `paddingSideMap`), populated by
  // elementSchemas.js#normalizeElementSettingsV3 from the SAME legacy digit
  // that already drives top/bottom (the old shorthand applied one value to
  // all four sides, so there is only one source value to translate, not an
  // independent horizontal setting). No suppression baseline for the same
  // reason as top/bottom above — nothing claims a default element
  // padding-inline anywhere.
  if (kind === 'element') {
    walkTierDedup(paddingSideMap(settings, 'inline'), (value, tier) => {
      const resolved = resolveSpace(value);
      if (resolved === null) reportReject('element', 'padding', `padding.${tier}.inline`, tier, value); // Spec C, Task 5
      return resolved;
    }, 'padding-inline', undefined, pushSection);
  }

  // Overlap (Spec 2026-07-31): margin-top — resolveSpace covers the named
  // steps AND {raw}, including negative values (RAW_VALUE_RE allows '-80px').
  // No default-suppression baseline (`undefined`): an untouched container's
  // marginTop is a genuinely empty tier map (nothing to suppress against),
  // same reasoning as column `order`/`alignSelf` above.
  walkTierDedup(settings.marginTop, (value, tier) => {
    const resolved = resolveSpace(value);
    if (resolved === null) reportReject('container', 'marginTop', `marginTop.${tier}`, tier, value); // Spec C, Task 5
    return resolved;
  }, 'margin-top', undefined, pushSection);

  // Layer: strict whitelist 1|2|3. z-index needs a positioned ancestor to
  // apply, so position:relative rides in the same rule. relative without
  // offsets does not move anything — harmless when nothing overlaps. A
  // mid-cascade explicit 'auto' entry resets both (Fix-Runde 1, see
  // layerDeclarationsByTier's own comment) — not a plain walkTierDedup call
  // for the same reason height isn't (see below).
  layerDeclarationsByTier(settings, pushSection);

  // Height (Spec 2026-07-31): replaces the retired .v2-full class bridge.
  // Emits min-height plus the flex/valign trio the class used to provide.
  // F6 done right (see heightDeclarationsByTier's own comment): at a tier
  // whose EFFECTIVE display is 'none' nothing is emitted (the hide wins); at
  // visible tiers the trio lands AFTER the display walk's un-hide value in
  // the same rule (this call runs strictly after displayDeclarationsByTier
  // above, into the SAME pushSection/sectionByTier arrays), so display:flex
  // wins by in-rule order instead of the old specificity hack. `displayByTier`
  // is the SAME `effectiveDisplayByTier(settings)` instance the caller
  // (emitNodeCss) already computed for the row/column reversal handling above
  // — reused here, not recomputed, so the two can never drift apart. `null`
  // for kind:'element' (no container `height` field exists there anyway, see
  // v2ContainerUtils.ts — the tier-map guard inside heightDeclarationsByTier
  // returns immediately before ever touching displayByTier in that case).
  heightDeclarationsByTier(settings, displayByTier, pushSection);

  // Free positioning (Spec 2026-07-31): MUST run AFTER layerDeclarationsByTier
  // above — see placementDeclarationsByTier's own "Layer interplay" comment —
  // so `position:absolute` reliably wins the same-tier, same-rule tie-break
  // against layer's own `position:relative`. `displayByTier` is `null` for
  // kind:'element' (no container `placement` field exists there either — the
  // tier-map guard inside placementDeclarationsByTier returns immediately
  // before ever touching displayByTier in that case, same as height above).
  placementDeclarationsByTier(settings, displayByTier, pushSection);

  // maxWidth: container-only, untiered (a single value, not a V2TierMap) —
  // always base-tier. Fix-Runde 3 (Important I3): on the ROW, not the
  // section — `.v2-bg` (the background layer) and the section's own
  // `background-color` inline style live on the SECTION, so a `max-width` on
  // the section clipped the background down to the narrowed content width;
  // the row is the actual content wrapper this replaces the old Bootstrap
  // wrapper for, so capping it there keeps the section (and its background)
  // full-bleed, matching the retired wrapper's own behaviour.
  if (kind === 'container') {
    maxWidthDeclarations(settings.maxWidth).forEach((decl) => pushRow('base', decl));
  }

  // Task 5 (v2-css elements): `width` (a percentage, 1-100) and `align`
  // (box alignment via `margin-inline`, for when `width` is reduced below
  // 100%) are ELEMENT-ONLY — a container's own `width` field is a different,
  // unrelated domain (the retained 'container'/'container-fluid' string enum,
  // v2ContainerUtils.ts) and a container has no `align` field at all, so
  // gating on `kind === 'element'` (rather than relying on the settings
  // object simply lacking these keys) makes the container/element split
  // explicit here the same way maxWidth's container-only gate is explicit
  // just above, instead of leaving it as an implicit consequence of which
  // fields happen to be populated upstream.
  //
  // Neither property has a "default value" to suppress against — the same
  // reasoning as `order` above: an untouched element has NO entry at any
  // tier for either (elementSchemas.js#normalizeElementSettingsV3 only ever
  // populates a tier when the legacy widthMd/align field was actually set),
  // so passing `undefined` as walkTierDedup's default disables suppression
  // without needing (or wanting) a v2-base.scss backing rule: nothing is
  // ever silently relying on an implicit default here.
  if (kind === 'element') {
    walkTierDedup(settings.width, (value, tier) => {
      const resolved = resolveElementWidth(value);
      if (resolved === null) reportReject('element', 'width', `width.${tier}`, tier, value); // Spec C, Task 5
      return resolved;
    }, 'width', undefined, pushSection);
    walkTierDedup(settings.align, (value, tier) => {
      const resolved = resolveElementAlign(value);
      if (resolved === null) reportReject('element', 'align', `align.${tier}`, tier, value); // Spec C, Task 5
      return resolved;
    }, 'margin-inline', undefined, pushSection);
  }

  return { sectionByTier, rowByTier };
}

/**
 * The effective (cascade-resolved) `display` value at EVERY tier — never
 * sparse, unlike `settings.display` itself. Tier N's value is either its
 * own explicit, whitelisted setting, or the value carried forward from the
 * nearest lower tier that had one, starting from the row's own true default
 * (`DEFAULT_DECLARATIONS.display`, i.e. `grid`).
 *
 * Needed (Fix-Runde 2) because a column's `span` must be expressed
 * differently depending on which layout mode is ACTUALLY active at its
 * tier — grid vs flex — and the tier that changes `display` is not
 * necessarily the same tier that sets `span`. Container-only: an element
 * has no columns to resolve this for.
 *
 * Exported (Fix-Runde 3, Critical 3): v2ContainerUtils.ts's applySettings
 * reuses this same cascade to decide, per tier, whether a container is
 * effectively hidden — the canvas-only `data-v2-hidden` marker (see there)
 * is derived from it rather than re-implementing the same cascade logic a
 * second time.
 *
 * Fix-Runde 5 ("justify-flex" task): this cascade result also has the
 * distribution-switches-to-flex override (module header, `
 * applyJustifyOverride`, gated by `canAutoSwitchByTier`) applied — a tier
 * whose effective `justifyContent` deviates from `flex-start` is reported
 * as `'flex'` here even when `settings.display` itself never said so (or
 * explicitly said `'grid'`), UNLESS the tier is (still) hidden, its summed
 * column span is already 12 (full width — see `canAutoSwitchByTier`), or
 * its effective `flexDirection` isn't `'row'`. This is safe for the OTHER
 * caller above (the hidden-marker derivation only ever compares against
 * `'none'`, a value this override never introduces or removes) and a no-op
 * for `kind:'element'` settings (no `justifyContent` field exists there at
 * all, see elementSchemas.js), so both existing call sites keep working
 * unchanged.
 */
export function effectiveDisplayByTier(settings) {
  const tierMap = settings.display;
  const justifyByTier = effectiveJustifyContentByTier(settings);
  const autoSwitchByTier = canAutoSwitchByTier(settings);
  const result = {};
  let effective = DEFAULT_DECLARATIONS.display;
  TIERS.forEach((tier) => {
    if (tierMap && typeof tierMap === 'object' && tierMap[tier] !== undefined) {
      const resolved = sanitizeEnum('display', tierMap[tier]);
      if (resolved !== null) effective = resolved;
    }
    result[tier] = applyJustifyOverride(effective, justifyByTier[tier], autoSwitchByTier[tier]);
  });
  return result;
}

/** Which span form a tier's effective display implies; anything else: no span. */
const SPAN_FORM_BY_DISPLAY = { flex: 'flex', grid: 'grid' };

/**
 * `span` declarations across all tiers. Its CSS FORM depends on the row's
 * EFFECTIVE `display` at that tier (Fix-Runde 2), not just on whether the
 * span VALUE changed: `grid-column:span N` only means anything once the row
 * is actually `display:grid` (its default); under an explicit `display:flex`
 * tier, the equivalent is a `flex-basis` percentage of the 12-track model
 * instead. Because the FORM can change at a tier where the span VALUE
 * itself is untouched (display flips, span doesn't), plain "same resolved
 * value as the tier below = skip" dedupe is not enough on its own here — a
 * form change alone must also force re-emission, so this tracks the last
 * EFFECTIVE `(span, form)` pair (Fix-Runde 3: tracked whether or not it was
 * actually emitted — same "effective carried forward, regardless of
 * suppression" rule every other property's walk already follows, needed so
 * a later tier can correctly tell "same as the (possibly suppressed) base
 * value" apart from a genuine deviation) rather than just the last value.
 *
 * `display: 'block'`/`'none'` have no span-equivalent concept (neither
 * grid-column nor flex-basis means anything there) — span emission is
 * simply skipped for such a tier; the cascade value is still tracked so a
 * later tier returning to grid/flex picks up correctly.
 *
 * Fix-Runde 4 (review-fix3.md, Critical 1): the base-tier default-suppression
 * is gated on the effective FORM being `grid`, not applied unconditionally.
 * `SPAN_DEFAULT` (full width, 12/12) is only actually the row's DEFAULT under
 * `v2-base.scss`'s `[data-v2-col]{grid-column:span 12}` rule (see
 * DEFAULT_DECLARATIONS's own doc comment) — a rule that, like every other
 * declaration this module suppresses, only applies once the row is really a
 * grid. Under an explicit base-tier `display:flex`, `span:12` has NO backing
 * default at all (no `[data-v2-col]{flex:...}` rule exists or could, since
 * flex-basis is a fraction computed from the tier's OWN span value, not a
 * fixed default) — suppressing it there previously produced a column with
 * NEITHER `grid-column` (wrong form) NOR `flex-basis` (suppressed as if
 * `grid-column`'s default covered it) — two content-width columns instead of
 * two full-width ones. Suppression now only ever fires for the grid form.
 */
function spanDeclarationsByTier(col, displayByTier, pushDecl) {
  let effectiveSpan;
  let effective;
  let hasEffective = false;
  TIERS.forEach((tier) => {
    const raw = col.span ? col.span[tier] : undefined;
    if (raw !== undefined) {
      if (Number.isInteger(raw) && raw >= 1 && raw <= 12) {
        effectiveSpan = raw;
      } else {
        reportReject('column', 'span', `span.${tier}`, tier, raw); // Spec C, Task 5
      }
    }
    if (effectiveSpan === undefined) return;
    const display = displayByTier[tier];
    // Flach statt verschachtelt: der Vue-2-Host lintet diese Datei mit
    // no-nested-ternary und bricht sonst den Webpack-Build ab.
    const form = SPAN_FORM_BY_DISPLAY[display] ?? null;
    if (form === null) return;
    const isRepeat = tier === 'base'
      ? form === 'grid' && effectiveSpan === SPAN_DEFAULT
      : hasEffective && effective.span === effectiveSpan && effective.form === form;
    effective = { span: effectiveSpan, form };
    hasEffective = true;
    if (isRepeat) return;
    pushDecl(tier, form === 'grid'
      ? `grid-column:span ${effectiveSpan}`
      // Flex fallback: a fixed fraction of the row's 12-track model,
      // expressed as flex-basis (grow/shrink pinned to 0 so the column
      // doesn't stretch to fill leftover space the way an ordinary flex
      // item would). With `gap` set, flex columns can wrap to a new line
      // earlier than the equivalent grid columns would — percentage widths
      // don't subtract the gap the way `grid-template-columns` tracks do.
      // That is inherent to flex's own box model, not a bug to compensate
      // for here.
      : `flex:0 0 calc(${effectiveSpan} / 12 * 100%)`);
  });
}

/**
 * Column-level analogue of `paddingSideMap` above (same one-line body,
 * different source object): builds a per-tier `{tier -> value}` map for one
 * padding side out of `col.padding` (a per-tier `{top, bottom}` map — see
 * `V2ColumnSettings['padding']` in v2ContainerUtils.ts), so `walkTierDedup`
 * can apply the exact same cascade/dedupe rule to column padding as it does
 * to every other tiered property here. Task 6 Fix-Runde 1 (review Important
 * I1) — see `columnDeclarationsByTier`'s own comment for the fuller context.
 */
function columnPaddingSideMap(col, side) {
  if (!col.padding || typeof col.padding !== 'object') return null;
  const map = {};
  TIERS.forEach((tier) => {
    const tierPadding = col.padding[tier];
    if (tierPadding && typeof tierPadding === 'object' && tierPadding[side] !== undefined) {
      map[tier] = tierPadding[side];
    }
  });
  return map;
}

/**
 * Declarations for one column (span/alignSelf/padding/order/position),
 * across all tiers, after dedupe. `position: sticky` additionally carries
 * its fixed top/align-self pair on whichever tier it is (still) emitted on.
 *
 * `firstReverseTier` (Fix Round 2, `null` when the row is never
 * grid-natively reversed — see `firstGridRowReverseTier`): when set, every
 * column gets one explicit `direction:ltr` reset at exactly that tier,
 * restoring normal text direction for this column's own content against the
 * row's `direction:rtl` (see `rowDirectionDeclarationsByTier`). The SAME
 * tier for every column of the row — computed once by the caller
 * (`columnEntriesByTier`), not re-derived per column.
 */
function columnDeclarationsByTier(col, displayByTier, firstReverseTier) {
  const byTier = {};
  const pushDecl = (tier, decl) => {
    if (!byTier[tier]) byTier[tier] = [];
    byTier[tier].push(decl);
  };

  spanDeclarationsByTier(col, displayByTier, pushDecl);
  if (firstReverseTier !== null) pushDecl(firstReverseTier, 'direction:ltr');

  // `align-self` (Task 6 Fix-Runde 1, review Important I1): the container
  // settings panel's breakpoint-tier switcher lets an editor set a column's
  // `alignSelf` per tier (V2ContainerSettingsControl.vue's
  // `columnAlignValue`/`updateColumnAlign`) — before this fix this emitter
  // had NO concept of column `alignSelf` at all (grep: zero hits), so every
  // tier the panel offered was inert: the control changed nothing in the
  // canvas or on the published page, which previously got its (base-tier
  // only) `align-self` from a DOM class bridge instead (see
  // v2ContainerUtils.ts's `applyColumnSettings`, now retired for this field
  // in the same commit — see that file's own comment on why). Walked the
  // same way as every other enum-typed tiered property: `sanitizeEnum` +
  // `walkTierDedup`.
  //
  // No base-tier default-suppression baseline (`defaultValue` is
  // `undefined`, same reasoning `order`'s own walk below and Task 5's
  // element-padding walk already use): `col.alignSelf`'s genuine default is
  // a genuinely EMPTY map (see `getDefaultColumnSettings()` — no value at
  // all, not the literal 'stretch'), and there is no static
  // `[data-v2-col]{align-self:...}` rule in v2-base.scss backing 'stretch'
  // as a fixed default the way `DEFAULT_DECLARATIONS.alignItems` backs the
  // ROW's own 'stretch' default. A column's "stretch" look today comes from
  // the ROW's `align-items` (itself user-editable — see the `alignItems`
  // walk in `nodeDeclarationsByTier`), not from any property of the column
  // itself; suppressing an explicit base-tier `align-self:stretch` on the
  // assumption that it always matches the row's default would silently drop
  // a genuine override the moment `alignItems` itself moved away from
  // 'stretch'.
  //
  // Precedence vs. `position:sticky`'s own forced `align-self:flex-start`
  // (below, the `position` walk): pushed BEFORE it, deliberately — for a
  // tier that sets BOTH an explicit `alignSelf` and `position:sticky`,
  // sticky's own forced declaration lands LATER in that tier's declaration
  // array and therefore wins the "last declaration of the same property
  // inside one rule" tie-break (same CSS parsing rule every browser already
  // applies within a single rule, no special-casing needed here). Sticky's
  // whole purpose is pinning the column to `flex-start` on the cross axis
  // while the page scrolls past it — an editor picking a different
  // `alignSelf` at the SAME tier a column turns sticky must not be able to
  // silently defeat that. A LATER tier's own explicit `alignSelf` (with
  // sticky unchanged, still cascading forward from the earlier tier) is
  // unaffected by this: it lands in its own, later, separately-emitted
  // `@media` block, which legitimately wins the normal CSS cascade (equal
  // specificity, later rule wins in the concatenated stylesheet) — a
  // deliberate, unspecial-cased consequence of how every tier already works
  // here, not a gap. Both halves are pinned down in cssEmitter.spec.ts's
  // "align-self vs. position:sticky" suite.
  walkTierDedup(
    col.alignSelf,
    (value, tier) => {
      const resolved = sanitizeEnum('alignSelf', value);
      if (resolved === null) reportReject('column', 'alignSelf', `alignSelf.${tier}`, tier, value); // Spec C, Task 5
      return resolved;
    },
    'align-self',
    undefined,
    pushDecl,
  );

  // Column padding-top/padding-bottom (Task 6 Fix-Runde 1, review Important
  // I1): same fix, same reasoning, for the panel's per-tier column padding
  // control. Uses the exact same `var(--wm-space-X, <legacy px>)` scale
  // (`resolveSpace()`, including its `{raw: '23px'}` custom-value escape
  // hatch) as the container's own gap/padding-top/padding-bottom above — one
  // scale, one resolver, for every spacing value this module ever emits. No
  // suppression baseline, for the same "genuinely empty default map, no
  // static default rule to suppress against" reason as `alignSelf` above —
  // `getDefaultColumnSettings()` ships `padding: {}`, not a named step.
  //
  // v2ContainerUtils.ts's inline-style padding bridge — the ONLY thing that
  // ever rendered column padding, base tier only, until this fix — is
  // retired in the same commit, mirroring exactly how `span`/`order`/
  // `position:sticky` lost their own DOM bridges once THIS emitter took them
  // over (see that file's own "CSS EMISSION" header comment). Leaving that
  // inline style in place instead would have been strictly worse than not
  // extending the emitter at all: an inline `element.style.paddingTop` beats
  // ANY external stylesheet rule regardless of specificity or media query,
  // so it would have permanently pinned column padding to its base-tier
  // value and made every non-base padding tier dead on arrival — in the
  // canvas (`useV2CssEmission.ts`'s live `<style id="wm-v2">` tag) AND on
  // the published page.
  // 'inline' is a third, independent side key in the same per-tier padding
  // object ({ top, bottom, inline }) — emitted as `padding-inline`
  // (left+right in one declaration), mirroring the section-level
  // padding-inline walk in nodeDeclarationsByTier. The template literal
  // resolves to exactly that property name for side='inline'.
  ['top', 'bottom', 'inline'].forEach((side) => {
    walkTierDedup(
      columnPaddingSideMap(col, side),
      (value, tier) => {
        const resolved = resolveSpace(value);
        if (resolved === null) reportReject('column', 'padding', `padding.${tier}.${side}`, tier, value); // Spec C, Task 5
        return resolved;
      },
      `padding-${side}`,
      undefined,
      pushDecl,
    );
  });

  // Column aspect-ratio (hero spec 2026-08-14): `col.ratio` arrives here
  // already in min-width form (Task 1's `toEmitterSettings`), 'auto' as the
  // explicit cascade stopper — same walk shape as every other tiered column
  // property above, no base-tier suppression baseline (see `resolveRatio`'s
  // own comment for why 'auto' is a real, meaningful value here, not a gap).
  walkTierDedup(col.ratio, (value, tier) => {
    const resolved = resolveRatio(value);
    if (resolved === null) reportReject('column', 'ratio', `ratio.${tier}`, tier, value); // Spec C, Task 5
    return resolved;
  }, 'aspect-ratio', undefined, pushDecl);

  // Column min height (2026-09-22): the section's own height values, but a
  // plain walk -- no flex/valign trio and no display coupling, the column
  // is a grid item whose content flows as before. 'auto' is a real value
  // here (min-height:auto), so it resets a wider tier's own entry.
  walkTierDedup(col.height, (value, tier) => {
    if (value === 'auto') return 'auto';
    const resolved = resolveHeight(value);
    if (resolved === null) reportReject('column', 'height', `height.${tier}`, tier, value);
    return resolved;
  }, 'min-height', undefined, pushDecl);

  // `order` has no notion of a "default value" to suppress against (any
  // integer is a valid, meaningful position) — this walk just emits it,
  // tier by tier, whatever `col.order` already contains. Fix-Runde 3
  // (Important I2): the OLD `v2-order-sm-N` class this replaces only ever
  // applied under `@media (max-width: 767.98px)` (v2-layout.scss), i.e. it
  // was implicitly "mobile order only" — a bare `order.base:N` with no
  // higher-tier override means native CSS carries that same order forward
  // to EVERY breakpoint above it too, silently re-ordering desktop columns
  // that were never meant to move. This module has no way to tell "the
  // caller meant this as mobile-only" from the tier map alone, so the fix is
  // upstream, at the two places that WRITE `order.base` for a column
  // (v2ContainerUtils.ts's `normalizeSettingsV3` legacy `orderSm` migration,
  // and V2ContainerSettingsControl.vue's `updateColumnOrder`): both now also
  // write an explicit `md:0` reset alongside `base`, restoring the original
  // "mobile order only" meaning without this emitter needing to know why.
  walkTierDedup(
    col.order,
    (value, tier) => {
      if (Number.isInteger(value) && value >= 0 && value <= 99) return String(value);
      reportReject('column', 'order', `order.${tier}`, tier, value); // Spec C, Task 5
      return null;
    },
    'order',
    undefined,
    pushDecl,
  );
  walkTierDedup(
    col.position,
    (value, tier) => {
      const resolved = sanitizeEnum('position', value);
      if (resolved === null) reportReject('column', 'position', `position.${tier}`, tier, value); // Spec C, Task 5
      return resolved;
    },
    'position',
    undefined,
    (tier, decl) => {
      pushDecl(tier, decl);
      if (decl === 'position:sticky') {
        pushDecl(tier, 'top:calc(var(--nav-height, 0px) + 16px)');
        pushDecl(tier, 'align-self:flex-start');
      }
    },
  );

  return byTier;
}

/**
 * Column entries with their per-tier declarations. The array index is
 * preserved from the source `columns` array (used for `data-v2-col`), even
 * though malformed entries are filtered out. `displayByTier` (see
 * `effectiveDisplayByTier`) is shared across every column of this node —
 * it's the same row for all of them. `reversedByTier` (Fix Round 2, see
 * `gridRowReverseByTier`) likewise — reduced here, once, to the single
 * shared `firstReverseTier` every column needs (see
 * `firstGridRowReverseTier`/`columnDeclarationsByTier`'s own comments).
 */
function columnEntriesByTier(settings, displayByTier, reversedByTier) {
  if (!Array.isArray(settings.columns)) return [];
  const firstReverseTier = firstGridRowReverseTier(reversedByTier);
  return settings.columns
    .map((col, index) => ((col && typeof col === 'object')
      ? { index, byTier: columnDeclarationsByTier(col, displayByTier, firstReverseTier) }
      : null))
    .filter((entry) => entry !== null);
}

export function emitNodeCss(id, settings, kind, onReject) {
  // onReject (Spec C, Task 5): `activeReject` is module-level `let` state,
  // but its lifetime is scoped to exactly ONE call of this function by the
  // try/finally below — set here, unconditionally cleared back to `null`
  // before returning, on every exit path including a thrown error. This IS
  // per-call semantics, not persistent module state that could leak between
  // calls or let two consumers (say, the canvas's live emission and a
  // batch checker run) contaminate each other: emission is entirely
  // synchronous, start to finish — nothing below ever awaits, defers, or
  // returns control to the event loop mid-way — so no other emitNodeCss/
  // emitPageCss call can possibly interleave with this one's own `try`
  // block and observe or clobber this call's listener. The alternative —
  // threading an `onReject` parameter through the 30+ internal
  // `*DeclarationsByTier`/`resolve*`/`sanitizeEnum` functions this module
  // already has, all the way down to wherever each one's own whitelist
  // rejects a value — would touch every one of their signatures and call
  // sites for a callback only these two PUBLIC entry points ever accept;
  // this single scoped variable reads the caller's `onReject` exactly once
  // and lets every internal gate reach it for free via `reportReject`.
  // `emitPageCss` below does not repeat this pattern itself: it forwards
  // its own `onReject` argument straight through to each per-node call of
  // THIS function, which then does its own scoping per node — one single
  // owner of the try/finally, not two.
  const wrapped = wrapOnReject(onReject);
  // No listener (the overwhelmingly common call): `activeReject` stays
  // `null`, exactly its resting state — `reportReject`'s own `if
  // (!activeReject) return;` guard short-circuits before doing any work at
  // all, matching the design's "without a callback: zero overhead" rule.
  activeReject = wrapped ? { onReject: wrapped, nodeId: typeof id === 'string' ? id : undefined } : null;
  try {
    // emitNodeCssBody (the pre-existing implementation, unchanged below,
    // just renamed) is defined directly underneath this wrapper — safe to
    // call from here for the same forward-reference reason as every other
    // such call in this file (see e.g. resolvePlacementCoords's own note).
    // eslint-disable-next-line no-use-before-define
    return emitNodeCssBody(id, settings, kind);
  } finally {
    activeReject = null;
  }
}

function emitNodeCssBody(id, settings, kind) {
  if (typeof id !== 'string' || !ID_RE.test(id)) return '';
  if (!settings || typeof settings !== 'object') return '';
  // Task 5 Fix-Runde 1 (review Critical C1): BOTH of the review's two
  // independent fixes are applied together, deliberately, not either/or.
  //
  // (a) — done in the SCSS, not here: the retained legacy `.v2-element`
  // rules (v2-layout.scss) and the generic `section.section{width:100%}`
  // rules (user.scss) are now scoped away from migrated markup
  // (`:not([data-v2-id])` / `:not(.v2-element[data-v2-id])`) — the two
  // states (legacy-styled vs. emission-styled) are meant to be mutually
  // exclusive per element (v2ElementUtils.ts#applyElementSettings,
  // v2-layout.scss's own "genau einem der beiden Zustaende" comment), and
  // this is what actually makes that true: after the guard, neither named
  // competitor matches a migrated element at all, at any specificity.
  //
  // (b) — here: for `kind === 'element'` ONLY, the selector itself gains
  // `.v2-element` (class) and `[data-wps-settings]` (attribute presence)
  // ahead of the id attribute, raising specificity the same way Task 5
  // originally did — but keyed on DIFFERENT markers than that original fix
  // used. Task 5 raised it via `section[data-wps-version="2"]
  // [data-wps-element]`; Task 7's review (Critical C1) found that selector
  // can never match on a PUBLISHED page at all:
  // `cleanSnippetHtmlForDisplay` (cleanHtmlForDisplay.ts — the function that
  // produces `display_data.content`, the only HTML the publish-side
  // collector, buildPageV2Css.js, ever sees) unconditionally strips
  // `data-wps-version` (step 19) and strips `data-wps-element` too
  // (`removeDataWpsAttributes`, because `element` is not part of
  // `PRESERVE_WPS_ATTR_RE`) — verified empirically (jsdom, the real cleaner,
  // a real fixture): 0 matches in the cleaned document. Every migrated
  // element therefore published with NO width/padding/align/hide-on-mobile
  // at all: it matched neither this selector (attributes gone) nor the
  // legacy fallback (correctly excluded by part (a)).
  // The three markers this selector uses instead all demonstrably SURVIVE
  // `cleanSnippetHtmlForDisplay` (see publishSelectorSurvival.spec.ts, which
  // runs the real cleaner and then asserts this exact selector matches in
  // the cleaned document):
  //   - `.v2-element` — a CLASS, added once by
  //     v2ElementUtils.ts#applyElementSettings and never removed again; the
  //     cleaner's class-removal steps each target specific unrelated
  //     classes (cke_editable, webmag_selected, fr-*, wps-aos-*, ...), never
  //     this one;
  //   - `[data-v2-id="ID"]` — not a `data-wps-*` attribute at all, so
  //     `removeDataWpsAttributes` never even inspects it;
  //   - `[data-wps-settings]` — IS a `data-wps-*` attribute, but is
  //     explicitly listed in `PRESERVE_WPS_ATTR_RE` (`...|settings|...`).
  // `data-wps-settings`'s presence on any node this function runs for is
  // additionally guaranteed by construction, not just by the cleaner: both
  // collectors (buildPageV2Css.js and useV2CssEmission.ts) only ever call
  // `emitNodeCss` for nodes their own `[data-v2-id][data-wps-settings]`
  // query already matched — the attribute this selector requires and the
  // attribute that gated collecting the node in the first place are the
  // SAME one. Specificity: unchanged at (0,3,1) — one class, two
  // attributes, one type selector — so every specificity guarantee below
  // still holds unmodified; only WHICH three markers earn that specificity
  // changed.
  //
  // (a) alone was found, empirically, not to be enough: deriving the full
  // competitor list from the COMPILED theme CSS (not just the two rules the
  // review named — see v2BaseSpecificity.spec.ts's derived check) turned up
  // `sizing.scss`'s `.h-100-all section { width: 100% }` (0,1,1) — an
  // unrelated, pre-existing, opt-in utility class with no knowledge of v2
  // elements at all, but which structurally matches any `<section>` and, at
  // (0,1,1), already outranks the bare `[data-v2-id="ID"]` (0,1,0) this
  // selector used to be. Before this task such a rule could never have won
  // against a v2-element regardless (the OLD `.v2-element`-scoped rule was
  // always at least (0,2,1)); gating alone would have quietly reintroduced
  // exactly that exposure for every migrated element sitting under an
  // ancestor with an unrelated utility class this migration was never
  // supposed to interact with. Raising this selector's own specificity
  // above every real element-scoped legacy rule (0,3,1 ties the highest
  // named competitor, `.pgs-article section.section.v2-element`, and beats
  // every plain `section`/`.class section` utility like `.h-100-all`'s)
  // closes that gap without having to chase down and gate every such
  // utility individually.
  //
  // Fix-Runde 2 (review I2 follow-up): the container's own section selector
  // is narrowed the same way, for the same reason. It used to be left at the
  // bare `[data-v2-id="ID"]` (0,1,0) on the theory that cssEmitter.js never
  // emits `width` on a container's own section, so a generic `section` rule
  // like `.h-100-all` had nothing there to outrank. That reasoning missed
  // that the container's OWN section bucket also carries
  // display:none/display:block (hide-on-mobile/-desktop) and
  // padding-block/padding-inline (see nodeDeclarationsByTier) — real,
  // customer-visible layout — and at (0,1,0) it loses any tie against a
  // single-class customer rule (`.section{...}`, a Tailwind `p-0` utility)
  // appended after it, and loses outright against anything ≥(0,1,1). Mirror
  // the element fix: key on `section[data-wps-v2-container][data-v2-id="ID"]
  // [data-wps-settings]` instead. All three markers survive
  // `cleanSnippetHtmlForDisplay` for the same reasons as the element
  // selector above — `data-wps-v2-container` is explicitly preserved
  // (`PRESERVE_WPS_ATTR_RE`'s `...|v2-container|...`) on both an outer and a
  // NESTED container after cleaning (publishSelectorSurvival.spec.ts), and
  // `[data-wps-settings]` is guaranteed by construction the same way it is
  // for elements: both collectors only ever call `emitNodeCss` for a node
  // their own `[data-v2-id][data-wps-settings]` query already matched,
  // container or element alike. Specificity: (0,3,1), identical to the
  // element selector and to the highest named legacy competitor — this
  // narrows the accidental-collision threshold from "any one class" down to
  // "three simple selectors", the same margin elements already have; it does
  // NOT close the risk outright (a customer rule at ≥(0,4,0), or
  // `!important`, still wins) — that residual is accepted, not solved, same
  // as it is for elements.
  const sectionSelector = kind === 'element'
    ? `section.v2-element[data-v2-id="${id}"][data-wps-settings]`
    : `section[data-wps-v2-container][data-v2-id="${id}"][data-wps-settings]`;
  const isContainer = kind === 'container';

  // The real DOM is `section[data-v2-id] > .container > [data-v2-row]` — a
  // fixed, shallow depth of exactly 2 (mirrors findRow()'s own invariant in
  // v2ContainerUtils.ts: the row always sits on a flat path directly under
  // the container's own container-div, never deeper, never shallower).
  // Chaining TWO child combinators (not a bare descendant space) is
  // load-bearing, not stylistic: it is what keeps this selector from
  // reaching a NESTED v2-container's own row/columns. Because a column can
  // itself contain another whole v2-container snippet, a nested row always
  // sits at least 4 levels further down (column > ... > nested section >
  // nested container-div > nested row) — a bare descendant combinator
  // cannot tell "my own row" apart from "a row belonging to a nested
  // container that happens to live inside my subtree", but this fixed-depth
  // chain can: it structurally cannot match anything deeper than exactly 2
  // levels below THIS section's own id. Same reasoning one level further for
  // the column selector below (exactly 3 levels: section > * > row > col).
  const rowSelector = `${sectionSelector} > * > [data-v2-row]`;

  // Computed ONCE (Fix Round 2) and shared by the row's own reversal
  // declarations (`nodeDeclarationsByTier`) and the columns' span form +
  // `ltr` reset (`columnEntriesByTier`) — `null` for `kind:'element'`,
  // which has no row/columns to compute a display cascade for at all (see
  // `effectiveDisplayByTier`'s own "container-only" note).
  const displayByTier = isContainer ? effectiveDisplayByTier(settings) : null;
  const { sectionByTier, rowByTier } = nodeDeclarationsByTier(settings, kind, displayByTier);
  const columnEntries = isContainer
    ? columnEntriesByTier(settings, displayByTier, gridRowReverseByTier(displayByTier, effectiveFlexDirectionByTier(settings)))
    : [];

  const blocks = TIERS
    .map((tier) => {
      const rules = [];
      const sectionDecls = sectionByTier[tier];
      if (sectionDecls && sectionDecls.length > 0) rules.push(`${sectionSelector}{${sectionDecls.join(';')}}`);
      const rowDecls = isContainer ? rowByTier[tier] : null;
      if (rowDecls && rowDecls.length > 0) rules.push(`${rowSelector}{${rowDecls.join(';')}}`);
      columnEntries.forEach(({ index, byTier }) => {
        const colDecls = byTier[tier];
        if (colDecls && colDecls.length > 0) {
          rules.push(`${rowSelector} > [data-v2-col="${index}"]{${colDecls.join(';')}}`);
        }
      });
      if (rules.length === 0) return null;
      return tier === 'base'
        ? rules.join('')
        : `@media (min-width:${TIER_MIN_WIDTH[tier]}px){${rules.join('')}}`;
    })
    .filter((block) => block !== null);

  // fillHeight (hero spec 2026-08-14): make the sole .container child grow
  // to the section's min-height and the row fill the container, so columns
  // stretch to the full height. Unconditional (no media query): at tiers
  // without an active height the section is display:block, so the flex
  // properties are inert; .container as flex-column with the row as its
  // only child renders identically to block. Pushed onto `blocks` (not a
  // per-tier `rules` array) precisely so it lands once, un-wrapped by any
  // `@media` block — the per-tier loop above re-runs per breakpoint, which
  // this setting has no notion of. `isContainer`-gated: fillHeight is a
  // container-only setting and `rowSelector` targets a row that never
  // exists under a `kind:'element'` node's own selector.
  if (isContainer && settings.fillHeight === true) {
    // Task 1 (2026-08-17-v2-bootstrap-css-exit): v2-frame/v2-frame-fluid are
    // additive aliases of container/container-fluid (v2ContainerUtils.ts#
    // applySettings stamps both pairs in lockstep) — included here so this
    // rule keeps matching once a container's markup is switched over for
    // real in a later task, without needing a change here.
    blocks.push(`${sectionSelector} > .container, ${sectionSelector} > .container-fluid, ${sectionSelector} > .v2-frame, ${sectionSelector} > .v2-frame-fluid{flex:1 1 auto;display:flex;flex-direction:column;min-height:0}`);
    blocks.push(`${rowSelector}{flex:1 1 auto}`);
  }

  return blocks.join('');
}

/**
 * The DEFAULT LAYER: the handful of rules every suppressed base-tier default
 * in this module silently assumes are already in force (see
 * `DEFAULT_DECLARATIONS`'s own doc comment for the per-rule reasoning, and
 * `spanDeclarationsByTier`/`SPAN_DEFAULT` for the column half).
 *
 * "Spalte auf 6/12 wird gespeichert, aber nicht angezeigt" defect: those rules
 * were authored ONLY in
 * `files/wps/_modules_dev/templates/global_snippets2/scss/custom/components/
 * required/v2-base.scss` — a SCSS source that has to be compiled by the
 * template toolchain (`files/gulpfile.js`, task `sass2`) into
 * `files/wps/_modules/**` before any browser ever sees it. Nothing in the app
 * build, the PHP asset image (`files/Dockerfile-php` merely COPIES the
 * pre-built `files/` tree) or CI compiles it, so between authoring that file
 * and the next manual theme rebuild, EVERY environment serves a stylesheet
 * with zero `[data-v2-row]`/`[data-v2-col]` rules in it — verified against
 * the running dev asset server (`localhost:8081`, the exact URL
 * `assetManifest.js#buildTemplateCssUrls` points the canvas at): 534 KB of
 * CSS, 0 occurrences of `data-v2`.
 *
 * The consequence is not a missing default but a DEAD FEATURE: without
 * `[data-v2-row]{display:grid;grid-template-columns:repeat(12,...)}` the row
 * is an ordinary block box, so the `grid-column:span N` this module emits for
 * a column width is inert CSS and every column renders at full width — the
 * reported "column stays at 12/12", at every breakpoint, no matter what the
 * settings say. Container default padding/gap were equally dead for the same
 * reason (the `var(--wm-space-*, <px>)` fallbacks kept the NON-default steps
 * working, which is why only column width looked broken).
 *
 * Emitting the same rules from here removes that deploy-order coupling
 * outright: the emitted stylesheet is now self-sufficient in BOTH consumers
 * (the canvas's live `<style id="wm-v2">` via useV2CssEmission.ts, and the
 * publish path via buildPageV2Css.js), and stays correct whether or not the
 * theme has been rebuilt. Every rule below is a byte-for-byte mirror of
 * v2-base.scss's own — same selectors, same `:where()` neutralisation (so a
 * default can never outrank an emitted value, see v2BaseSpecificity.spec.ts),
 * and values read straight out of `DEFAULT_DECLARATIONS`/`SPAN_DEFAULT` so
 * the two can't drift. Once the theme IS rebuilt, the identical rules simply
 * coexist: same declarations, same (zero) specificity, no visual change.
 *
 * `min-width:0` is the one rule deliberately kept OUTSIDE `:where()` — it is
 * structural (grid/flex items default to `min-width:auto` and overflow), has
 * no settings field and no emitted counterpart, exactly as v2-base.scss
 * documents.
 *
 * The `:root` custom-property scale (`--wm-space-*`/`--wm-content-*`) from
 * that same file is NOT mirrored here: every declaration this module emits
 * references those through a `var(name, <legacy px>)` fallback pair, so they
 * are optional by construction.
 */
export const V2_BASE_DEFAULTS = [
  ':where([data-v2-row]){'
    + `display:${DEFAULT_DECLARATIONS.display};`
    + `grid-template-columns:${DEFAULT_DECLARATIONS.gridTemplateColumns};`
    + `flex-wrap:${DEFAULT_DECLARATIONS.flexWrap};`
    + `align-items:${DEFAULT_DECLARATIONS.alignItems};`
    + `flex-direction:${DEFAULT_DECLARATIONS.flexDirection};`
    + `justify-content:${DEFAULT_DECLARATIONS.justifyContent};`
    + `${gapDeclarations(DEFAULT_DECLARATIONS.gap).join(';')}}`,
  ':where([data-wps-v2-container][data-v2-id]){'
    + `padding-top:${DEFAULT_DECLARATIONS.paddingTop};`
    + `padding-bottom:${DEFAULT_DECLARATIONS.paddingBottom}}`,
  `:where([data-v2-col]){grid-column:span ${SPAN_DEFAULT}}`,
  '[data-v2-col]{min-width:0}',
].join('');

/**
 * Whether a collected node is one this module can actually emit for — the
 * same two gates `emitNodeCss` applies before doing anything (a well-formed
 * id and an object-shaped settings bag). Used to decide whether the page
 * carries any V2 node at all, and therefore whether the default layer above
 * belongs in the output: a page with no V2 nodes must keep emitting the
 * empty string it always did (the canvas writes this straight into
 * `<style id="wm-v2">`, the publish path into the page's CSS blob).
 *
 * Deliberately NOT "did this node emit any declarations": an untouched
 * all-default container emits NOTHING of its own (cssEmitter.spec.ts's
 * Critical-1 guard) and is precisely the node that needs the default layer
 * most — it is 100% reliant on it for its grid, gap and padding.
 */
function isEmittableNode(node) {
  return typeof node.id === 'string' && ID_RE.test(node.id)
    && !!node.settings && typeof node.settings === 'object';
}

/**
 * `onReject` (Spec C, Task 5, optional): forwarded VERBATIM to each per-node
 * `emitNodeCss` call below — this function does no scoping of its own (no
 * `activeReject` assignment here at all). `emitNodeCss` already owns the
 * full per-call try/finally (see its own comment), and every node in a page
 * is emitted by exactly one such call in sequence, never concurrently — so
 * letting each call do its own scoping, one node at a time, already gives
 * every rejection the correct `nodeId` for free, with nothing left for this
 * function to add.
 */
export function emitPageCss(nodes, onReject) {
  if (!Array.isArray(nodes)) return '';
  const realNodes = nodes.filter((node) => node !== null && typeof node === 'object');
  const nodeCss = realNodes
    .map((node) => emitNodeCss(node.id, node.settings, node.kind, onReject))
    .join('');
  if (!realNodes.some(isEmittableNode)) return nodeCss;
  return V2_BASE_DEFAULTS + nodeCss;
}
